import React, { useState, useEffect } from 'react';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import VerificationBanner from '../components/VerificationBanner';
import {
  getCreatorDashboard,
  getCreatorCampaigns,
  getCreatorTasks,
  getCreatorInvoices,
  getCreatorAnalytics,
  getCreatorEarnings,
  getInstagramAuthUrl,
  getInstagramStatus,
  getInstagramProfile,
  getInstagramInsights,
  getInstagramComments,
  disconnectInstagram,
  getCreatorPreferences,
  updateCreatorPreferences,
  getCreatorInvitations,
  respondToInvitation,
  verifyPublishedContent,
  submitContent,
  getCreatorPerformance,
  refreshCreatorPerformance,
  getCreatorPaymentEarnings,
  getCreatorPaymentDetails,
  saveCreatorPaymentDetails,
  isAuthenticated,
  getUserType
} from '../api/api';
import './CreatorDashboard.css';

// Keep MIN length aligned with backend INSTAGRAM_MEDIA_SHORTCODE_MIN_LEN (scheduler/tasks.py).
const INSTAGRAM_PUBLISHED_URL_RE = /instagram\.com\/(?:p|reel|reels|tv)\/([A-Za-z0-9_-]+)/i;
const INSTAGRAM_MEDIA_SHORTCODE_MIN_LEN = 10;

function validateInstagramPublishedUrlShape(url) {
  const trimmed = (url || '').trim();
  const m = trimmed.match(INSTAGRAM_PUBLISHED_URL_RE);
  if (!m) {
    return {
      ok: false,
      message:
        'Paste a full Instagram post or reel link (for example instagram.com/p/… or instagram.com/reel/…).',
    };
  }
  if (m[1].length < INSTAGRAM_MEDIA_SHORTCODE_MIN_LEN) {
    return {
      ok: false,
      message:
        'That link looks incomplete or truncated. Open your post on Instagram, copy the entire URL from the address bar, and paste it here.',
    };
  }
  return { ok: true };
}

function CreatorDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Check authentication - redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated()) {
      // Save the current URL to redirect back after login
      const returnUrl = location.pathname + location.search;
      navigate(`/login?redirect=${encodeURIComponent(returnUrl)}&type=creator`);
    } else if (getUserType() !== 'creator') {
      // If logged in but not as a creator, redirect to appropriate dashboard
      navigate('/brand-dashboard');
    }
  }, [navigate, location]);
  
  // Initialize activeTab from URL or default to 'overview'
  const [activeTab, setActiveTab] = useState(() => {
    const tabFromUrl = searchParams.get('tab');
    const validTabs = ['overview', 'invitations', 'campaigns', 'analytics', 'payments', 'instagram', 'preferences', 'payment'];
    return validTabs.includes(tabFromUrl) ? tabFromUrl : 'overview';
  });
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [campaigns, setCampaigns] = useState({ active: [], pending: [], completed: [] });
  const [tasks, setTasks] = useState({ pending: [], in_progress: [], completed: [] });
  const [invoices, setInvoices] = useState({ all: [] });
  const [analytics, setAnalytics] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [paymentEarnings, setPaymentEarnings] = useState(null);
  const [instagramStatus, setInstagramStatus] = useState({ connected: false, username: null });
  const [instagramConnecting, setInstagramConnecting] = useState(false);
  const [instagramMessage, setInstagramMessage] = useState(null);
  const [instagramProfile, setInstagramProfile] = useState(null);
  const [instagramLoading, setInstagramLoading] = useState(false);
  const [instagramInsights, setInstagramInsights] = useState(null);
  const [instagramComments, setInstagramComments] = useState(null);
  const [instagramInsightsLoading, setInstagramInsightsLoading] = useState(false);
  const [instagramCommentsLoading, setInstagramCommentsLoading] = useState(false);
  
  // Preferences state
  const [preferences, setPreferences] = useState({
    collaboration_types: [],
    content_categories: [],
    rate_per_post: '',
    rate_per_reel: '',
    rate_per_story: '',
    available_for_campaigns: true,
    preferred_brand_industries: [],
    excluded_brand_industries: [],
    languages_spoken: [],
    target_locations: [],
    min_campaign_budget: '',
    response_time_hours: 24
  });
  const [preferencesLoading, setPreferencesLoading] = useState(false);
  const [preferencesSaving, setPreferencesSaving] = useState(false);
  const [preferencesMessage, setPreferencesMessage] = useState(null);
  const [initialPreferences, setInitialPreferences] = useState(null);
  
  // Check for unsaved preferences changes
  const hasUnsavedPreferences = initialPreferences && JSON.stringify(preferences) !== JSON.stringify(initialPreferences);
  
  // Campaign invitations state
  const [invitations, setInvitations] = useState([]);
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  const [invitationResponding, setInvitationResponding] = useState(null);
  
  // Content submission state
  const [contentSubmitting, setContentSubmitting] = useState(false);
  const [contentUrl, setContentUrl] = useState('');
  const [platformUrl, setPlatformUrl] = useState('');
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [selectedCampaignForSubmission, setSelectedCampaignForSubmission] = useState(null);
  const [submissionNotes, setSubmissionNotes] = useState('');
  const [submissionError, setSubmissionError] = useState('');
  const [submissionType, setSubmissionType] = useState('url'); // 'url' or 'file'
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Campaign pipeline tab filter
  const [campaignPipelineTab, setCampaignPipelineTab] = useState('active');
  const [performanceData, setPerformanceData] = useState(null);
  const [refreshingPerformance, setRefreshingPerformance] = useState(null);
  
  // Payment filter for earnings tab
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all'); // 'all', 'pending', 'completed', 'failed'
  
  // Payment details state
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [paymentDetailsLoading, setPaymentDetailsLoading] = useState(false);
  const [paymentDetailsSaving, setPaymentDetailsSaving] = useState(false);
  const [paymentDetailsMessage, setPaymentDetailsMessage] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('upi');
  const [paymentForm, setPaymentForm] = useState({
    upi_id: '',
    account_holder_name: '',
    account_number: '',
    ifsc_code: '',
    bank_name: '',
    account_type: 'savings',
    pan_number: ''
  });

  const creatorId = localStorage.getItem('userId') || 'mock-creator-id';

  // Update URL when tab changes
  useEffect(() => {
    const currentTab = searchParams.get('tab');
    if (currentTab !== activeTab) {
      setSearchParams({ tab: activeTab }, { replace: true });
    }
  }, [activeTab, searchParams, setSearchParams]);

  useEffect(() => {
    // Check for Instagram OAuth callback
    const params = new URLSearchParams(location.search);
    const instagramConnected = params.get('instagram_connected');
    const instagramError = params.get('instagram_error');
    const instagramUsername = params.get('instagram_username');

    if (instagramConnected === 'true') {
      setInstagramMessage({ type: 'success', text: `Instagram account @${instagramUsername} connected successfully!` });
      setInstagramStatus({ connected: true, username: instagramUsername });
      window.history.replaceState({}, document.title, '/creator-dashboard');
    } else if (instagramError) {
      setInstagramMessage({ type: 'error', text: `Failed to connect Instagram: ${instagramError}` });
      window.history.replaceState({}, document.title, '/creator-dashboard');
    }

    fetchDashboardData();
    fetchInstagramStatus();
    fetchPreferences();
    fetchInvitations();
    fetchPaymentDetails();
  }, [location]);

  const fetchInvitations = async () => {
    setInvitationsLoading(true);
    try {
      const response = await getCreatorInvitations(creatorId);
      setInvitations(response.data.invitations || []);
    } catch (err) {
      console.log('Could not fetch invitations');
    } finally {
      setInvitationsLoading(false);
    }
  };

  // Fetch payment details
  const fetchPaymentDetails = async () => {
    setPaymentDetailsLoading(true);
    try {
      const response = await getCreatorPaymentDetails(creatorId);
      if (response.data.has_payment_details && response.data.payment_details) {
        setPaymentDetails(response.data.payment_details);
        setPaymentMethod(response.data.payment_details.payment_method || 'upi');
        // Populate form with existing data
        setPaymentForm(prev => ({
          ...prev,
          upi_id: response.data.payment_details.upi_id || '',
          account_holder_name: response.data.payment_details.account_holder_name || '',
          account_number: response.data.payment_details.account_number || '',
          ifsc_code: response.data.payment_details.ifsc_code || '',
          bank_name: response.data.payment_details.bank_name || '',
          account_type: response.data.payment_details.account_type || 'savings',
          pan_number: response.data.payment_details.pan_number || ''
        }));
      }
    } catch (err) {
      console.log('Could not fetch payment details');
    } finally {
      setPaymentDetailsLoading(false);
    }
  };

  // Save payment details
  const handleSavePaymentDetails = async (e) => {
    e.preventDefault();
    setPaymentDetailsSaving(true);
    setPaymentDetailsMessage(null);

    try {
      // Validation
      if (paymentMethod === 'upi') {
        if (!paymentForm.upi_id) {
          setPaymentDetailsMessage({ type: 'error', text: 'Please enter your UPI ID' });
          setPaymentDetailsSaving(false);
          return;
        }
        // UPI ID validation: must contain @ and follow format like name@bank
        const upiRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9]+$/;
        if (!upiRegex.test(paymentForm.upi_id)) {
          setPaymentDetailsMessage({ type: 'error', text: 'Invalid UPI ID format (e.g., yourname@okaxis, mobile@paytm)' });
          setPaymentDetailsSaving(false);
          return;
        }
      } else {
        if (!paymentForm.account_holder_name || !paymentForm.account_number || !paymentForm.ifsc_code) {
          setPaymentDetailsMessage({ type: 'error', text: 'Please fill all required bank details' });
          setPaymentDetailsSaving(false);
          return;
        }
        if (paymentForm.ifsc_code.length !== 11) {
          setPaymentDetailsMessage({ type: 'error', text: 'IFSC code must be 11 characters' });
          setPaymentDetailsSaving(false);
          return;
        }
        // IFSC format validation: 4 letters + 0 + 6 alphanumeric
        const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
        if (!ifscRegex.test(paymentForm.ifsc_code)) {
          setPaymentDetailsMessage({ type: 'error', text: 'Invalid IFSC format (e.g., SBIN0001234)' });
          setPaymentDetailsSaving(false);
          return;
        }
        // Account number validation: 9-18 digits
        if (paymentForm.account_number.length < 9 || paymentForm.account_number.length > 18) {
          setPaymentDetailsMessage({ type: 'error', text: 'Account number should be 9-18 digits' });
          setPaymentDetailsSaving(false);
          return;
        }
      }
      
      // PAN validation if provided
      if (paymentForm.pan_number) {
        const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
        if (!panRegex.test(paymentForm.pan_number)) {
          setPaymentDetailsMessage({ type: 'error', text: 'Invalid PAN format (e.g., ABCDE1234F)' });
          setPaymentDetailsSaving(false);
          return;
        }
      }

      const details = {
        payment_method: paymentMethod,
        upi_id: paymentMethod === 'upi' ? paymentForm.upi_id : null,
        account_holder_name: paymentMethod === 'bank' ? paymentForm.account_holder_name : null,
        account_number: paymentMethod === 'bank' ? paymentForm.account_number : null,
        ifsc_code: paymentMethod === 'bank' ? paymentForm.ifsc_code : null,
        bank_name: paymentMethod === 'bank' ? paymentForm.bank_name : null,
        account_type: paymentMethod === 'bank' ? paymentForm.account_type : null,
        pan_number: paymentForm.pan_number || null
      };

      const response = await saveCreatorPaymentDetails(creatorId, details);
      
      if (response.data.success) {
        setPaymentDetails(response.data.payment_details);
        setPaymentDetailsMessage({ type: 'success', text: 'Payment details saved successfully!' });
      }
    } catch (err) {
      setPaymentDetailsMessage({ 
        type: 'error', 
        text: err.response?.data?.detail || 'Failed to save payment details' 
      });
    } finally {
      setPaymentDetailsSaving(false);
    }
  };

  const handleInvitationResponse = async (invitationId, action) => {
    // Check for payment details before accepting
    if (action === 'accept' && !paymentDetails) {
      setInstagramMessage({ 
        type: 'error', 
        text: 'Please add your payment details before accepting campaigns. Go to Settings → Payment Details.' 
      });
      // Switch to payment tab
      setActiveTab('payment');
      return;
    }
    
    setInvitationResponding(invitationId);
    try {
      await respondToInvitation(invitationId, action);
      // Refresh invitations
      fetchInvitations();
      // Refresh dashboard data to update active campaigns immediately
      fetchDashboardData();
      // Show success message
      setInstagramMessage({ 
        type: 'success', 
        text: `Campaign invitation ${action}ed successfully!` 
      });
    } catch (err) {
      setInstagramMessage({ 
        type: 'error', 
        text: `Failed to ${action} invitation` 
      });
    } finally {
      setInvitationResponding(null);
    }
  };

  const fetchInstagramStatus = async () => {
    try {
      const response = await getInstagramStatus(creatorId);
      setInstagramStatus(response.data);
      
      // If connected, fetch full profile + insights + comments
      if (response.data.connected) {
        fetchInstagramProfile();
        fetchInstagramInsights();
        fetchInstagramComments();
      }
    } catch (err) {
      console.log('Could not fetch Instagram status');
    }
  };

  const fetchInstagramProfile = async () => {
    setInstagramLoading(true);
    try {
      const response = await getInstagramProfile(creatorId);
      setInstagramProfile(response.data);
    } catch (err) {
      console.log('Could not fetch Instagram profile');
    } finally {
      setInstagramLoading(false);
    }
  };

  // Fetches account- and media-level insights (reach, profile views, etc.).
  // Backed by the instagram_business_manage_insights permission.
  const fetchInstagramInsights = async () => {
    setInstagramInsightsLoading(true);
    try {
      const response = await getInstagramInsights(creatorId);
      setInstagramInsights(response.data);
    } catch (err) {
      console.log('Could not fetch Instagram insights');
    } finally {
      setInstagramInsightsLoading(false);
    }
  };

  // Fetches comments on the creator's recent posts.
  // Backed by the instagram_business_manage_comments permission.
  const fetchInstagramComments = async () => {
    setInstagramCommentsLoading(true);
    try {
      const response = await getInstagramComments(creatorId);
      setInstagramComments(response.data);
    } catch (err) {
      console.log('Could not fetch Instagram comments');
    } finally {
      setInstagramCommentsLoading(false);
    }
  };

  const fetchPreferences = async () => {
    setPreferencesLoading(true);
    try {
      const response = await getCreatorPreferences(creatorId);
      if (response.data) {
        const loadedPreferences = {
          collaboration_types: response.data.collaboration_types || [],
          content_categories: response.data.content_categories || [],
          rate_per_post: response.data.rate_per_post || '',
          rate_per_reel: response.data.rate_per_reel || '',
          rate_per_story: response.data.rate_per_story || '',
          available_for_campaigns: response.data.available_for_campaigns !== false,
          preferred_brand_industries: response.data.preferred_brand_industries || [],
          excluded_brand_industries: response.data.excluded_brand_industries || [],
          languages_spoken: response.data.languages_spoken || [],
          target_locations: response.data.target_locations || [],
          min_campaign_budget: response.data.min_campaign_budget || '',
          response_time_hours: response.data.response_time_hours || 24
        };
        setPreferences(prev => ({ ...prev, ...loadedPreferences }));
        setInitialPreferences(prev => ({ ...preferences, ...loadedPreferences }));
      }
    } catch (err) {
      console.log('Could not fetch preferences');
    } finally {
      setPreferencesLoading(false);
    }
  };

  const handleSavePreferences = async () => {
    setPreferencesSaving(true);
    setPreferencesMessage(null);
    try {
      const prefsToSave = {
        ...preferences,
        rate_per_post: preferences.rate_per_post ? parseInt(preferences.rate_per_post) : null,
        rate_per_reel: preferences.rate_per_reel ? parseInt(preferences.rate_per_reel) : null,
        rate_per_story: preferences.rate_per_story ? parseInt(preferences.rate_per_story) : null,
        min_campaign_budget: preferences.min_campaign_budget ? parseInt(preferences.min_campaign_budget) : null,
        response_time_hours: parseInt(preferences.response_time_hours) || 24
      };
      
      await updateCreatorPreferences(creatorId, prefsToSave);
      setPreferencesMessage({ type: 'success', text: 'Preferences saved successfully!' });
      setInitialPreferences({ ...preferences }); // Reset initial to current (no unsaved changes)
      
      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setPreferencesMessage({ type: 'error', text: 'Failed to save preferences. Please try again.' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setPreferencesSaving(false);
    }
  };

  const handlePreferenceChange = (field, value) => {
    setPreferences(prev => ({ ...prev, [field]: value }));
  };

  const toggleArrayItem = (field, item) => {
    setPreferences(prev => {
      const current = prev[field] || [];
      if (current.includes(item)) {
        return { ...prev, [field]: current.filter(i => i !== item) };
      } else {
        return { ...prev, [field]: [...current, item] };
      }
    });
  };

  const handleInstagramConnect = async () => {
    setInstagramConnecting(true);
    try {
      const response = await getInstagramAuthUrl(creatorId, '/creator-dashboard');
      window.location.href = response.data.auth_url;
    } catch (err) {
      setInstagramMessage({ type: 'error', text: 'Failed to initiate Instagram connection' });
      setInstagramConnecting(false);
    }
  };

  const handleInstagramDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect your Instagram account?')) return;
    
    try {
      await disconnectInstagram(creatorId);
      setInstagramStatus({ connected: false, username: null });
      setInstagramMessage({ type: 'success', text: 'Instagram disconnected successfully' });
    } catch (err) {
      setInstagramMessage({ type: 'error', text: 'Failed to disconnect Instagram' });
    }
  };

  // Handle content submission
  const handleContentSubmission = async () => {
    // Validate based on submission type
    if (submissionType === 'url' && !contentUrl.trim()) {
      setSubmissionError('Please provide a content URL (Google Drive, Dropbox, etc.)');
      return;
    }
    
    if (submissionType === 'file' && !selectedFile) {
      setSubmissionError('Please select a file to upload');
      return;
    }
    
    if (!selectedCampaignForSubmission?.task?.id) {
      setSubmissionError('Task not found for this campaign');
      return;
    }
    
    setContentSubmitting(true);
    setSubmissionError('');
    setUploadProgress(0);
    
    try {
      let finalContentUrl = contentUrl;
      
      // If file upload, upload to server first
      if (submissionType === 'file' && selectedFile) {
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('creator_id', creatorId);
        formData.append('campaign_id', selectedCampaignForSubmission.id);
        
        const uploadResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/uploads/content`, {
          method: 'POST',
          body: formData,
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!uploadResponse.ok) {
          const errorData = await uploadResponse.json();
          throw new Error(errorData.detail || 'File upload failed');
        }
        
        const uploadData = await uploadResponse.json();
        finalContentUrl = uploadData.file_url;
        setUploadProgress(100);
      }
      
      const response = await submitContent({
        creator_id: creatorId,
        campaign_id: selectedCampaignForSubmission.id,
        task_id: selectedCampaignForSubmission.task.id,
        content_url: finalContentUrl,
        content_type: selectedCampaignForSubmission.content_types?.[0] || 'reel',
        notes: submissionNotes
      });
      
      if (response.data.success) {
        alert(`Content submitted successfully! Submission #${response.data.submission_number}. ${response.data.remaining_submissions} submissions remaining.`);
        setShowSubmissionModal(false);
        setContentUrl('');
        setSubmissionNotes('');
        setSelectedFile(null);
        setSubmissionType('url');
        setUploadProgress(0);
        setSelectedCampaignForSubmission(null);
        // Refresh campaigns data
        fetchDashboardData();
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to submit content';
      setSubmissionError(errorMsg);
    } finally {
      setContentSubmitting(false);
    }
  };

  // Handle refresh performance metrics (only Instagram metrics, not full page)
  const handleRefreshPerformance = async (trackingId) => {
    setRefreshingPerformance(trackingId);
    try {
      const res = await refreshCreatorPerformance(creatorId, trackingId);
      if (res.data.success) {
        // Only refresh performance data, not the entire page
        const perfRes = await getCreatorPerformance(creatorId);
        if (perfRes.data) {
          setPerformanceData(perfRes.data);
        }
      }
    } catch (err) {
      alert('Failed to refresh metrics: ' + (err.response?.data?.detail || err.message));
    } finally {
      setRefreshingPerformance(null);
    }
  };

  // Handle refresh ALL performance metrics (only Instagram metrics, not full page)
  const handleRefreshAllPerformance = async () => {
    if (!performanceData?.monitoring?.length) return;
    
    setRefreshingPerformance('all');
    try {
      // Refresh each tracking record
      for (const perf of performanceData.monitoring) {
        await refreshCreatorPerformance(creatorId, perf.id);
      }
      // Only refresh performance data, not the entire page
      const perfRes = await getCreatorPerformance(creatorId);
      if (perfRes.data) {
        setPerformanceData(perfRes.data);
      }
    } catch (err) {
      alert('Failed to refresh metrics: ' + (err.response?.data?.detail || err.message));
    } finally {
      setRefreshingPerformance(null);
    }
  };

  // Handle publish URL submission
  const handlePublishUrlSubmission = async () => {
    if (!instagramStatus.connected || instagramStatus.needs_reconnect || instagramStatus.token_valid === false) {
      setSubmissionError('Please reconnect your Instagram account first.');
      return;
    }
    if (!platformUrl.trim()) {
      setSubmissionError('Please provide the published Instagram URL');
      return;
    }

    const urlShape = validateInstagramPublishedUrlShape(platformUrl);
    if (!urlShape.ok) {
      setSubmissionError(urlShape.message);
      return;
    }

    setContentSubmitting(true);
    setSubmissionError('');

    try {
      const response = await verifyPublishedContent({
        campaign_id: selectedCampaignForSubmission.id,
        creator_id: creatorId,
        platform_url: platformUrl
      });

      if (response.data.success) {
        alert('Published URL submitted! Performance monitoring has started.');
        setShowPublishModal(false);
        setPlatformUrl('');
        setSelectedCampaignForSubmission(null);
        fetchDashboardData();
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Backend may return a structured { code, message } dict for IG gates
      if (detail && typeof detail === 'object') {
        const { code, message } = detail;
        const friendly = {
          instagram_not_connected: 'Connect your Instagram account before submitting a published URL.',
          instagram_token_expired: 'Your Instagram session expired. Please reconnect Instagram and try again.',
          url_not_owned: 'This post isn\'t on your connected Instagram account. Make sure you\'re pasting a URL from @' + (instagramStatus.username || 'your connected account') + '.',
          verification_failed: 'We couldn\'t reach Instagram to verify this URL. Please try again in a moment.',
        }[code];
        // invalid_url: prefer API message (truncated vs generic wording)
        setSubmissionError(
          code === 'invalid_url' && message
            ? message
            : friendly || message || 'Failed to submit published URL'
        );
        // If the backend says the creator needs to reconnect, update local
        // state so the modal switches to the Connect-Instagram CTA.
        if (code === 'instagram_not_connected' || code === 'instagram_token_expired') {
          setInstagramStatus((prev) => ({
            ...prev,
            connected: code === 'instagram_token_expired',
            needs_reconnect: true,
            token_valid: false,
          }));
        }
      } else {
        setSubmissionError(detail || 'Failed to submit published URL');
      }
    } finally {
      setContentSubmitting(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [dashRes, campaignsRes, tasksRes, invoicesRes, analyticsRes, earningsRes, perfRes, paymentRes] = await Promise.all([
        getCreatorDashboard(creatorId).catch(() => ({ data: getMockDashboard() })),
        getCreatorCampaigns(creatorId).catch(() => ({ data: getMockCampaigns() })),
        getCreatorTasks(creatorId).catch(() => ({ data: getMockTasks() })),
        getCreatorInvoices(creatorId).catch(() => ({ data: getMockInvoices() })),
        getCreatorAnalytics(creatorId).catch(() => ({ data: getMockAnalytics() })),
        getCreatorEarnings(creatorId).catch(() => ({ data: getMockEarnings() })),
        getCreatorPerformance(creatorId).catch(() => ({ data: null })),
        getCreatorPaymentEarnings(creatorId).catch(() => ({ data: null }))
      ]);

      setDashboardData(dashRes.data);
      setCampaigns(campaignsRes.data);
      setTasks(tasksRes.data);
      setInvoices(invoicesRes.data);
      if (perfRes.data) {
        setPerformanceData(perfRes.data);
      }
      setAnalytics(analyticsRes.data);
      setEarnings(earningsRes.data);
      if (paymentRes.data) {
        setPaymentEarnings(paymentRes.data);
      }
    } catch (err) {
      console.error('Error fetching dashboard:', err);
      setDashboardData(getMockDashboard());
      setCampaigns(getMockCampaigns());
      setTasks(getMockTasks());
      setInvoices(getMockInvoices());
      setAnalytics(getMockAnalytics());
      setEarnings(getMockEarnings());
    } finally {
      setLoading(false);
    }
  };

  const getMockDashboard = () => ({
    overview: {
      active_campaigns: 3,
      pending_tasks: 5,
      pending_payments: 2,
      total_earned: 125000,
      this_month_earned: 42000
    },
    upcoming_deadlines: [
      { title: 'Instagram Reel - Product Review', due: '2024-04-15', campaign: 'Summer Launch' },
      { title: 'YouTube Short - Unboxing', due: '2024-04-20', campaign: 'Tech Review' }
    ],
    notifications: [
      { type: 'payment', message: 'Payment of ₹25,000 received from Fashion Brand' },
      { type: 'campaign', message: 'New campaign invitation from TechCorp' }
    ]
  });

  const getMockCampaigns = () => ({
    active: [
      { id: '1', name: 'Summer Fashion Launch', brand_name: 'Fashion Co.', status: 'active', deliverables: 3, completed_deliverables: 1 },
      { id: '2', name: 'Tech Gadget Review', brand_name: 'TechCorp', status: 'active', deliverables: 2, completed_deliverables: 0 }
    ],
    pending_approval: [
      { id: '3', name: 'Food Delivery Campaign', brand_name: 'FoodieApp', status: 'pending_approval' }
    ],
    completed: [
      { id: '4', name: 'Winter Collection', brand_name: 'Fashion Co.', status: 'completed', earnings: 35000 }
    ]
  });

  const getMockTasks = () => ({
    pending: [
      { id: '1', title: 'YouTube Short - Unboxing', campaign_name: 'Tech Review', due_date: '2024-04-20', priority: 'medium' }
    ],
    in_progress: [
      { id: '2', title: 'Instagram Reel - Product Review', campaign_name: 'Summer Launch', due_date: '2024-04-15', priority: 'high' }
    ],
    completed: [
      { id: '3', title: 'Story Mention', campaign_name: 'Summer Launch', completed_date: '2024-04-10' }
    ],
    overdue: []
  });

  const getMockInvoices = () => ({
    drafted: [],
    sent: [{ id: 'inv-003', campaign_name: 'Food Delivery', amount: 15000, created_at: '2024-04-01' }],
    approved: [{ id: 'inv-002', campaign_name: 'Tech Review', amount: 35000, created_at: '2024-03-28' }],
    paid: [{ id: 'inv-001', campaign_name: 'Summer Launch', amount: 25000, paid_at: '2024-03-20' }],
    all: []
  });

  const getMockAnalytics = () => ({
    overview: {
      total_followers: 250000,
      total_posts: 156,
      avg_engagement_rate: 4.2,
      total_reach_30d: 750000,
      total_impressions_30d: 2000000
    },
    engagement: {
      total_likes_30d: 37500,
      total_comments_30d: 5000,
      total_shares_30d: 2000,
      total_saves_30d: 6250,
      total_views_30d: 625000
    },
    growth: {
      followers_gained_30d: 12500,
      net_growth_30d: 10000,
      growth_rate: 4.0
    },
    audience_sentiment: {
      positive: 68,
      neutral: 25,
      negative: 7
    }
  });

  const getMockEarnings = () => ({
    total_earned: 125000,
    pending_payments: 50000,
    upcoming_payouts: 35000,
    available_for_withdrawal: 112500,
    monthly_breakdown: [
      { month: 'Jan', amount: 25000 },
      { month: 'Feb', amount: 35000 },
      { month: 'Mar', amount: 42000 },
      { month: 'Apr', amount: 23000 }
    ]
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num;
  };

  // Reads a single numeric value out of an Instagram insight metric object,
  // handling both the legacy `values: [{value}]` shape and the newer
  // `total_value: {value}` shape returned by the Graph API.
  const readInsightValue = (metric) => {
    if (!metric) return 0;
    if (metric.total_value && typeof metric.total_value.value !== 'undefined') {
      return metric.total_value.value || 0;
    }
    if (Array.isArray(metric.values) && metric.values.length > 0) {
      return metric.values[0].value || 0;
    }
    return 0;
  };

  const prettifyMetricName = (name) =>
    (name || '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());

  if (loading) {
    return (
      <div className="creator-dashboard">
        <Navbar />
        <div className="dashboard-loading">
          <div className="loading-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="creator-dashboard">
      <Navbar />
      
      <div className="dashboard-container">
        {/* Sidebar */}
        <aside className="dashboard-sidebar">
          <div className="sidebar-header">
            <h2>Creator Dashboard</h2>
          </div>
          
          {/* Instagram Connection Status in Sidebar */}
          <div className="sidebar-instagram">
            {instagramStatus.connected ? (
              <div className="instagram-connected-badge">
                <span className="ig-icon">📸</span>
                <div className="ig-info">
                  <span className="ig-username">@{instagramStatus.username}</span>
                  <span className="ig-status">Connected</span>
                </div>
              </div>
            ) : (
              <button 
                className="sidebar-instagram-btn"
                onClick={handleInstagramConnect}
                disabled={instagramConnecting}
              >
                <span>📸</span>
                {instagramConnecting ? 'Connecting...' : 'Connect Instagram'}
              </button>
            )}
          </div>

          <nav className="sidebar-nav">
            <button 
              className={`nav-item ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              <span className="nav-icon">🏠</span>
              Overview
            </button>
            <button 
              className={`nav-item ${activeTab === 'invitations' ? 'active' : ''}`}
              onClick={() => setActiveTab('invitations')}
            >
              <span className="nav-icon">💌</span>
              Invitations
              {invitations.filter(i => i.status === 'pending').length > 0 && (
                <span className="badge highlight">{invitations.filter(i => i.status === 'pending').length}</span>
              )}
            </button>
            <button 
              className={`nav-item ${activeTab === 'campaigns' ? 'active' : ''}`}
              onClick={() => setActiveTab('campaigns')}
            >
              <span className="nav-icon">📋</span>
              My Campaigns
              {(campaigns.active?.length || 0) + (campaigns.published?.length || 0) > 0 && (
                <span className="badge">{(campaigns.active?.length || 0) + (campaigns.published?.length || 0)}</span>
              )}
            </button>
            <button
              className={`nav-item ${activeTab === 'analytics' ? 'active' : ''}`}
              onClick={() => setActiveTab('analytics')}
            >
              <span className="nav-icon">📊</span>
              Analytics
            </button>
            <button
              className={`nav-item ${activeTab === 'payments' ? 'active' : ''}`}
              onClick={() => setActiveTab('payments')}
            >
              <span className="nav-icon">💳</span>
              Payments
            </button>
            <button
              className={`nav-item ${activeTab === 'instagram' ? 'active' : ''}`}
              onClick={() => setActiveTab('instagram')}
            >
              <span className="nav-icon">🔗</span>
              Platforms
              {instagramStatus.needs_reconnect && <span className="badge warning">!</span>}
            </button>
            <button 
              className={`nav-item ${activeTab === 'preferences' ? 'active' : ''}`}
              onClick={() => setActiveTab('preferences')}
            >
              <span className="nav-icon">⚙️</span>
              Preferences
            </button>
            <button 
              className={`nav-item ${activeTab === 'payment' ? 'active' : ''} ${!paymentDetails ? 'needs-attention' : ''}`}
              onClick={() => setActiveTab('payment')}
            >
              <span className="nav-icon">🏦</span>
              Payment Details
              {!paymentDetails && <span className="badge warning">!</span>}
            </button>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="dashboard-main">
          <VerificationBanner />
          
          {/* Notification Messages */}
          {instagramMessage && (
            <div className={`dashboard-message ${instagramMessage.type}`}>
              <span>{instagramMessage.text}</span>
              <button onClick={() => setInstagramMessage(null)}>×</button>
            </div>
          )}

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="tab-content overview-page">
              <h1>Welcome Back!</h1>

              {/* Action Required Alerts */}
              <div className="alerts-section">
                {/* Payment Details Missing */}
                {!paymentDetails && (
                  <div className="alert-banner warning" onClick={() => setActiveTab('payment')} style={{ cursor: 'pointer' }}>
                    <span className="alert-icon">⚠️</span>
                    <div className="alert-content">
                      <h4>Payment Details Required</h4>
                      <p>Add your UPI or bank details to receive campaign payments</p>
                    </div>
                    <span className="alert-action">Add Now →</span>
                  </div>
                )}

                {/* Instagram Connection Prompt */}
                {!instagramStatus.connected && (
                  <div className="alert-banner info">
                    <span className="alert-icon">📸</span>
                    <div className="alert-content">
                      <h4>Connect Your Instagram</h4>
                      <p>Link your Instagram to verify content submissions</p>
                    </div>
                    <button
                      className="alert-btn"
                      onClick={handleInstagramConnect}
                      disabled={instagramConnecting}
                    >
                      {instagramConnecting ? 'Connecting...' : 'Connect'}
                    </button>
                  </div>
                )}

                {/* Instagram Reconnection Required */}
                {instagramStatus.needs_reconnect && (
                  <div className="alert-banner danger">
                    <span className="alert-icon">🔄</span>
                    <div className="alert-content">
                      <h4>Instagram Reconnection Required</h4>
                      <p>Your access has expired. Please reconnect.</p>
                    </div>
                    <button
                      className="alert-btn"
                      onClick={handleInstagramConnect}
                      disabled={instagramConnecting}
                    >
                      Reconnect
                    </button>
                  </div>
                )}
              </div>

              {/* Main Stats Grid */}
              <div className="stats-grid clickable">
                {/* Pending Invitations */}
                <div
                  className={`stat-card invitations ${invitations.filter(i => i.status === 'pending').length > 0 ? 'highlight' : ''}`}
                  onClick={() => setActiveTab('invitations')}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="stat-icon">💌</span>
                  <div className="stat-info">
                    <span className="stat-value">{invitations.filter(i => i.status === 'pending').length}</span>
                    <span className="stat-label">Pending Invitations</span>
                  </div>
                  {invitations.filter(i => i.status === 'pending').length > 0 && (
                    <span className="click-hint">View →</span>
                  )}
                </div>

                {/* Active Campaigns */}
                <div
                  className="stat-card campaigns"
                  onClick={() => setActiveTab('campaigns')}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="stat-icon">📋</span>
                  <div className="stat-info">
                    <span className="stat-value">{(campaigns.active?.length || 0) + (campaigns.published?.length || 0)}</span>
                    <span className="stat-label">Active Campaigns</span>
                  </div>
                </div>

                {/* Posts Monitored */}
                <div
                  className="stat-card analytics"
                  onClick={() => setActiveTab('analytics')}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="stat-icon">📊</span>
                  <div className="stat-info">
                    <span className="stat-value">{performanceData?.monitoring?.length || 0}</span>
                    <span className="stat-label">Posts Monitored</span>
                  </div>
                </div>

                {/* Total Earned */}
                <div
                  className="stat-card earnings"
                  onClick={() => setActiveTab('payments')}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="stat-icon">💰</span>
                  <div className="stat-info">
                    <span className="stat-value">{formatCurrency(paymentEarnings?.total_earned || 0)}</span>
                    <span className="stat-label">Total Earned</span>
                  </div>
                </div>
              </div>

              {/* Secondary Stats */}
              <div className="secondary-stats">
                <div className="mini-stat" onClick={() => setActiveTab('payments')} style={{ cursor: 'pointer' }}>
                  <span className="mini-icon">⏳</span>
                  <span className="mini-value">{formatCurrency(paymentEarnings?.pending_amount || 0)}</span>
                  <span className="mini-label">Pending Payments</span>
                </div>
                <div className="mini-stat" onClick={() => setActiveTab('campaigns')} style={{ cursor: 'pointer' }}>
                  <span className="mini-icon">✅</span>
                  <span className="mini-value">{campaigns.completed?.length || 0}</span>
                  <span className="mini-label">Completed</span>
                </div>
                <div className="mini-stat" onClick={() => setActiveTab('analytics')} style={{ cursor: 'pointer' }}>
                  <span className="mini-icon">👥</span>
                  <span className="mini-value">{formatNumber(instagramProfile?.followers_count || 0)}</span>
                  <span className="mini-label">Followers</span>
                </div>
                <div className="mini-stat">
                  <span className="mini-icon">📈</span>
                  <span className="mini-value">{analytics?.overview?.avg_engagement_rate || 0}%</span>
                  <span className="mini-label">Engagement</span>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="quick-actions-section">
                <h3>Quick Actions</h3>
                <div className="quick-actions-grid">
                  <button className="quick-action-btn" onClick={() => setActiveTab('invitations')}>
                    <span className="qa-icon">💌</span>
                    <span className="qa-label">View Invitations</span>
                  </button>
                  <button className="quick-action-btn" onClick={() => setActiveTab('campaigns')}>
                    <span className="qa-icon">📋</span>
                    <span className="qa-label">My Campaigns</span>
                  </button>
                  <button className="quick-action-btn" onClick={() => setActiveTab('payments')}>
                    <span className="qa-icon">💳</span>
                    <span className="qa-label">View Payments</span>
                  </button>
                  <button className="quick-action-btn" onClick={() => setActiveTab('analytics')}>
                    <span className="qa-icon">📊</span>
                    <span className="qa-label">Analytics</span>
                  </button>
                </div>
              </div>

              {/* Dashboard Grid */}
              <div className="dashboard-grid">
                <section className="section-card">
                  <h3>📅 Upcoming Deadlines</h3>
                  {(!dashboardData?.upcoming_deadlines || dashboardData?.upcoming_deadlines?.length === 0) ? (
                    <div className="empty-state-mini">
                      <span>✅</span>
                      <p>No upcoming deadlines</p>
                    </div>
                  ) : (
                    <div className="deadlines-list">
                      {dashboardData?.upcoming_deadlines?.map((deadline, idx) => (
                        <div key={idx} className="deadline-item">
                          <div className="deadline-info">
                            <h4>{deadline.title}</h4>
                            <span className="deadline-campaign">{deadline.campaign}</span>
                          </div>
                          <span className="deadline-date">{deadline.due}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </section>

                <section className="section-card">
                  <h3>🔔 Recent Activity</h3>
                  {(!dashboardData?.notifications || dashboardData?.notifications?.length === 0) ? (
                    <div className="empty-state-mini">
                      <span>📭</span>
                      <p>No recent activity</p>
                    </div>
                  ) : (
                    <div className="notifications-list">
                      {dashboardData?.notifications?.map((notif, idx) => (
                        <div key={idx} className={`notification-item ${notif.type}`}>
                          <span className="notif-icon">
                            {notif.type === 'payment' ? '💵' : notif.type === 'campaign' ? '📋' : '📢'}
                          </span>
                          <span className="notif-message">{notif.message}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              </div>

              {/* Profile Completion */}
              {(() => {
                const hasInstagram = instagramStatus.connected;
                const hasPaymentDetails = !!paymentDetails;
                const hasPreferences = preferences?.content_types?.length > 0;
                const completedSteps = [hasInstagram, hasPaymentDetails, hasPreferences].filter(Boolean).length;
                const totalSteps = 3;
                const percentage = Math.round((completedSteps / totalSteps) * 100);

                if (percentage === 100) return null;

                return (
                  <div className="profile-completion-card">
                    <div className="completion-header">
                      <h3>Complete Your Profile</h3>
                      <span className="completion-percentage">{percentage}%</span>
                    </div>
                    <div className="completion-bar">
                      <div className="completion-fill" style={{ width: `${percentage}%` }}></div>
                    </div>
                    <div className="completion-steps">
                      <div className={`step ${hasInstagram ? 'done' : ''}`} onClick={() => !hasInstagram && setActiveTab('instagram')}>
                        <span className="step-icon">{hasInstagram ? '✅' : '📸'}</span>
                        <span className="step-label">Connect Instagram</span>
                      </div>
                      <div className={`step ${hasPaymentDetails ? 'done' : ''}`} onClick={() => !hasPaymentDetails && setActiveTab('payment')}>
                        <span className="step-icon">{hasPaymentDetails ? '✅' : '🏦'}</span>
                        <span className="step-label">Add Payment Details</span>
                      </div>
                      <div className={`step ${hasPreferences ? 'done' : ''}`} onClick={() => !hasPreferences && setActiveTab('preferences')}>
                        <span className="step-icon">{hasPreferences ? '✅' : '⚙️'}</span>
                        <span className="step-label">Set Preferences</span>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Invitations Tab */}
          {activeTab === 'invitations' && (
            <div className="tab-content">
              <h1>Campaign Invitations</h1>
              <p className="tab-subtitle">Brands have invited you to participate in these campaigns</p>
              
              {invitationsLoading ? (
                <div className="loading-state">Loading invitations...</div>
              ) : invitations.length === 0 ? (
                <div className="empty-state">
                  <span>💌</span>
                  <h3>No pending invitations</h3>
                  <p>When brands invite you to campaigns, they'll appear here</p>
                </div>
              ) : (
                <div className="invitations-list-compact">
                  {invitations.map(invitation => (
                    <div key={invitation.id} className={`invitation-card-compact ${invitation.status}`}>
                      {/* Header Row */}
                      <div className="inv-header-row">
                        <div className="inv-brand-info">
                          <div className="inv-avatar">
                            {invitation.brand_name?.charAt(0) || 'B'}
                          </div>
                          <div className="inv-titles">
                            <h4>{invitation.campaign_name}</h4>
                            <span>by {invitation.brand_name}</span>
                            {invitation.brand_industry && (
                              <span className="inv-industry">{invitation.brand_industry}</span>
                            )}
                          </div>
                        </div>
                        <div className="inv-meta">
                          {/* Show "New" badge only for invitations created within last 48 hours */}
                          {invitation.sent_at && (new Date() - new Date(invitation.sent_at)) < 48 * 60 * 60 * 1000 && (
                            <span className="inv-badge new">New</span>
                          )}
                          {invitation.expires_at && (
                            <span className="inv-expires">Expires {new Date(invitation.expires_at).toLocaleDateString()}</span>
                          )}
                        </div>
                      </div>
                      
                      {/* Campaign Tags */}
                      <div className="inv-tags-row">
                        {invitation.niche && (
                          <span className="inv-tag niche">🎯 {invitation.niche}</span>
                        )}
                        {invitation.platform && (
                          <span className="inv-tag platform">📱 {invitation.platform}</span>
                        )}
                        {invitation.content_types?.map((type, i) => (
                          <span key={i} className="inv-tag content">{type}</span>
                        ))}
                      </div>
                      
                      {/* Description if available */}
                      {invitation.description && (
                        <p className="inv-description">{invitation.description}</p>
                      )}
                      
                      {/* Details Row */}
                      <div className="inv-details-row">
                        <div className="inv-detail">
                          <span className="inv-detail-label">⏰ Deadline</span>
                          <span className="inv-detail-value">{invitation.deadline || 14} days</span>
                        </div>
                        <div className="inv-detail payment-highlight">
                          <span className="inv-detail-label">💰 Payment</span>
                          <span className="inv-detail-value total">₹{invitation.base_payment?.toLocaleString()}</span>
                        </div>
                      </div>
                      
                      {/* Campaign Brief - Collapsible - Shows all script and brief details */}
                      <details className="inv-brief-details">
                        <summary>📋 View Campaign Brief & Script</summary>
                        <div className="inv-brief-content">
                          {/* Content Brief */}
                          {invitation.content_brief && (
                            <div className="brief-section">
                              <strong>📝 Content Brief:</strong>
                              <p>{invitation.content_brief}</p>
                            </div>
                          )}
                          
                          {/* Script */}
                          {invitation.content_script && (
                            <div className="brief-section script">
                              <strong>🎬 Script / Talking Points:</strong>
                              <div className="script-content">
                                {invitation.content_script}
                              </div>
                            </div>
                          )}
                          
                          {/* Key Messages */}
                          {invitation.key_messages?.length > 0 && (
                            <div className="brief-section">
                              <strong>💡 Key Messages:</strong>
                              <ul>
                                {invitation.key_messages.map((msg, i) => <li key={i}>{msg}</li>)}
                              </ul>
                            </div>
                          )}
                          
                          {/* Talking Points */}
                          {invitation.talking_points?.length > 0 && (
                            <div className="brief-section">
                              <strong>🎯 Talking Points:</strong>
                              <ul>
                                {invitation.talking_points.map((point, i) => <li key={i}>{point}</li>)}
                              </ul>
                            </div>
                          )}
                          
                          {/* Call to Action */}
                          {invitation.cta && (
                            <div className="brief-section cta">
                              <strong>📣 Call to Action (CTA):</strong>
                              <p>{invitation.cta}</p>
                            </div>
                          )}
                          
                          {/* Target Audience */}
                          {invitation.target_audience && (
                            <div className="brief-section">
                              <strong>👥 Target Audience:</strong>
                              <p>{invitation.target_audience}</p>
                            </div>
                          )}
                          
                          {/* Mood/Tone */}
                          {invitation.mood && (
                            <div className="brief-section">
                              <strong>🎭 Mood/Tone:</strong>
                              <p>{invitation.mood}</p>
                            </div>
                          )}
                          
                          {/* Style */}
                          {invitation.style && (
                            <div className="brief-section">
                              <strong>🎨 Style:</strong>
                              <p>{invitation.style}</p>
                            </div>
                          )}
                          
                          {/* Do's */}
                          {invitation.dos?.length > 0 && (
                            <div className="brief-section dos">
                              <strong>✅ Do's:</strong>
                              <ul>
                                {invitation.dos.map((item, i) => <li key={i}>{item}</li>)}
                              </ul>
                            </div>
                          )}
                          
                          {/* Don'ts */}
                          {invitation.donts?.length > 0 && (
                            <div className="brief-section donts">
                              <strong>❌ Don'ts:</strong>
                              <ul>
                                {invitation.donts.map((item, i) => <li key={i}>{item}</li>)}
                              </ul>
                            </div>
                          )}
                          
                          {/* Guidelines */}
                          {invitation.guidelines && (
                            <div className="brief-section">
                              <strong>📖 Brand Guidelines:</strong>
                              <p>{invitation.guidelines}</p>
                            </div>
                          )}
                          
                          {/* Hashtags */}
                          {invitation.hashtags?.length > 0 && (
                            <div className="brief-section">
                              <strong>#️⃣ Required Hashtags:</strong>
                              <div className="inv-hashtags">
                                {invitation.hashtags.map((tag, i) => (
                                  <span key={i} className="inv-hashtag">{tag}</span>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Mentions */}
                          {invitation.mentions?.length > 0 && (
                            <div className="brief-section">
                              <strong>@ Required Mentions:</strong>
                              <div className="inv-mentions">
                                {invitation.mentions.map((mention, i) => (
                                  <span key={i} className="inv-mention">{mention}</span>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Reference Links */}
                          {invitation.reference_links?.length > 0 && (
                            <div className="brief-section">
                              <strong>🔗 Reference Links:</strong>
                              <ul className="reference-links">
                                {invitation.reference_links.map((link, i) => (
                                  <li key={i}>
                                    <a href={link} target="_blank" rel="noopener noreferrer">{link}</a>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {/* Empty state if no brief info */}
                          {!invitation.content_brief && !invitation.content_script && !invitation.key_messages?.length && 
                           !invitation.talking_points?.length && !invitation.cta && !invitation.dos?.length && 
                           !invitation.donts?.length && !invitation.hashtags?.length && !invitation.mentions?.length && (
                            <div className="brief-empty">
                              <span>📄</span>
                              <p>No detailed brief available. Contact the brand for more information.</p>
                            </div>
                          )}
                        </div>
                      </details>
                      
                      {/* Payment Breakdown */}
                      <div className="inv-payment-breakdown">
                        <span>Fixed: ₹{invitation.fixed_payment?.toLocaleString()}</span>
                        <span className="separator">+</span>
                        <span>Bonus: ₹{invitation.performance_payment?.toLocaleString()}</span>
                        <span className="payment-note">(Fixed: weekly, Bonus: after monitoring)</span>
                      </div>
                      
                      {/* Actions */}
                      {invitation.status === 'pending' && (
                        <div className="inv-actions">
                          <button 
                            className="inv-btn decline"
                            onClick={() => handleInvitationResponse(invitation.id, 'reject')}
                            disabled={invitationResponding === invitation.id}
                          >
                            Decline
                          </button>
                          <button 
                            className="inv-btn accept"
                            onClick={() => handleInvitationResponse(invitation.id, 'accept')}
                            disabled={invitationResponding === invitation.id}
                          >
                            {invitationResponding === invitation.id ? 'Processing...' : '✓ Accept'}
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Campaigns Tab - Redesigned */}
          {activeTab === 'campaigns' && (
            <div className="tab-content campaigns-page-redesigned">
              <h1>My Campaigns</h1>
              <p className="tab-subtitle">Manage your campaigns, submit content, and track earnings</p>
              
              {/* Section 1: Urgency Alerts */}
              {(() => {
                const revisionNeeded = campaigns.active?.filter(c => c.task_status === 'revision_needed') || [];
                const dueSoon = campaigns.active?.filter(c => {
                  if (!c.task?.due_date) return false;
                  const dueDate = new Date(c.task.due_date);
                  const now = new Date();
                  const diffDays = Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24));
                  return diffDays <= 2 && diffDays >= 0;
                }) || [];
                const readyToPublish = campaigns.approved || [];
                
                if (revisionNeeded.length > 0 || dueSoon.length > 0 || readyToPublish.length > 0) {
                  return (
                    <div className="urgency-alerts-section">
                      <h3>⚠️ Attention Needed</h3>
                      <div className="urgency-alerts-grid">
                        {revisionNeeded.length > 0 && (
                          <div 
                            className="urgency-alert revision"
                            onClick={() => setCampaignPipelineTab('active')}
                          >
                            <span className="alert-icon">🔄</span>
                            <div className="alert-content">
                              <span className="alert-count">{revisionNeeded.length}</span>
                              <span className="alert-label">Revision Needed</span>
                            </div>
                          </div>
                        )}
                        {dueSoon.length > 0 && (
                          <div 
                            className="urgency-alert due-soon"
                            onClick={() => setCampaignPipelineTab('active')}
                          >
                            <span className="alert-icon">⏰</span>
                            <div className="alert-content">
                              <span className="alert-count">{dueSoon.length}</span>
                              <span className="alert-label">Due in 48 Hours</span>
                            </div>
                          </div>
                        )}
                        {readyToPublish.length > 0 && (
                          <div 
                            className="urgency-alert publish"
                            onClick={() => setCampaignPipelineTab('approved')}
                          >
                            <span className="alert-icon">🚀</span>
                            <div className="alert-content">
                              <span className="alert-count">{readyToPublish.length}</span>
                              <span className="alert-label">Ready to Publish</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
              
              {/* Section 2: Quick Stats Bar */}
              <div className="campaign-quick-stats">
                <div 
                  className={`quick-stat-item ${campaignPipelineTab === 'active' ? 'active' : ''}`}
                  onClick={() => setCampaignPipelineTab('active')}
                >
                  <span className="stat-icon">📝</span>
                  <span className="stat-count">{campaigns.active?.filter(c => c.task_status !== 'submitted').length || 0}</span>
                  <span className="stat-label">Active</span>
                </div>
                <div 
                  className={`quick-stat-item ${campaignPipelineTab === 'review' ? 'active' : ''}`}
                  onClick={() => setCampaignPipelineTab('review')}
                >
                  <span className="stat-icon">⏳</span>
                  <span className="stat-count">{campaigns.active?.filter(c => c.task_status === 'submitted').length || 0}</span>
                  <span className="stat-label">Under Review</span>
                </div>
                <div 
                  className={`quick-stat-item ${campaignPipelineTab === 'approved' ? 'active' : ''}`}
                  onClick={() => setCampaignPipelineTab('approved')}
                >
                  <span className="stat-icon">✅</span>
                  <span className="stat-count">{campaigns.approved?.length || 0}</span>
                  <span className="stat-label">Approved</span>
                </div>
                <div 
                  className={`quick-stat-item ${campaignPipelineTab === 'monitoring' ? 'active' : ''}`}
                  onClick={() => setCampaignPipelineTab('monitoring')}
                >
                  <span className="stat-icon">📊</span>
                  <span className="stat-count">{performanceData?.monitoring?.length || campaigns.published?.length || 0}</span>
                  <span className="stat-label">Monitoring</span>
                </div>
                <div 
                  className={`quick-stat-item ${campaignPipelineTab === 'completed' ? 'active' : ''}`}
                  onClick={() => setCampaignPipelineTab('completed')}
                >
                  <span className="stat-icon">🎉</span>
                  <span className="stat-count">{campaigns.completed?.length || 0}</span>
                  <span className="stat-label">Completed</span>
                </div>
                <div 
                  className={`quick-stat-item ${campaignPipelineTab === 'issues' ? 'active' : ''}`}
                  onClick={() => setCampaignPipelineTab('issues')}
                >
                  <span className="stat-icon">❌</span>
                  <span className="stat-count">{(campaigns.rejected?.length || 0) + (campaigns.cancelled?.length || 0)}</span>
                  <span className="stat-label">Issues</span>
                </div>
              </div>
              
              {/* Section 3: Earnings Summary
                  Source of truth is the new `/api/payments/creator-earnings/`
                  endpoint (paymentEarnings). The legacy `earnings` payload is
                  intentionally not used for the headline numbers because it
                  used to fall back to mock data. */}
              <div className="earnings-summary-card">
                <div className="earnings-summary-header">
                  <h3>💰 Earnings Overview</h3>
                </div>
                <div className="earnings-summary-grid">
                  <div className="earnings-item paid">
                    <span className="earnings-icon">✅</span>
                    <div className="earnings-details">
                      <span className="earnings-amount">{formatCurrency(paymentEarnings?.total_earned || 0)}</span>
                      <span className="earnings-label">Total Earned</span>
                    </div>
                  </div>
                  <div className="earnings-item pending">
                    <span className="earnings-icon">📊</span>
                    <div className="earnings-details">
                      <span className="earnings-amount">
                        {formatCurrency(paymentEarnings?.pending_amount || 0)}
                      </span>
                      <span className="earnings-label">Pending release</span>
                    </div>
                  </div>
                  <div className="earnings-item upcoming">
                    <span className="earnings-icon">🚀</span>
                    <div className="earnings-details">
                      <span className="earnings-amount">
                        {formatCurrency(paymentEarnings?.upcoming_amount || 0)}
                      </span>
                      <span className="earnings-label">Upcoming weeks</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Section 4: Tab-Based Campaign Pipeline */}
              <div className="campaign-pipeline-section">
                <div className="pipeline-tabs-creator">
                  <button 
                    className={`pipeline-tab-btn ${campaignPipelineTab === 'active' ? 'active' : ''}`}
                    onClick={() => setCampaignPipelineTab('active')}
                  >
                    <span className="tab-icon">📝</span>
                    <span className="tab-text">Active</span>
                    <span className="tab-badge">{campaigns.active?.filter(c => c.task_status !== 'submitted').length || 0}</span>
                  </button>
                  <button 
                    className={`pipeline-tab-btn ${campaignPipelineTab === 'review' ? 'active' : ''}`}
                    onClick={() => setCampaignPipelineTab('review')}
                  >
                    <span className="tab-icon">⏳</span>
                    <span className="tab-text">Under Review</span>
                    <span className="tab-badge">{campaigns.active?.filter(c => c.task_status === 'submitted').length || 0}</span>
                  </button>
                  <button 
                    className={`pipeline-tab-btn ${campaignPipelineTab === 'approved' ? 'active' : ''}`}
                    onClick={() => setCampaignPipelineTab('approved')}
                  >
                    <span className="tab-icon">✅</span>
                    <span className="tab-text">Ready to Publish</span>
                    <span className="tab-badge">{campaigns.approved?.length || 0}</span>
                  </button>
                  <button 
                    className={`pipeline-tab-btn ${campaignPipelineTab === 'monitoring' ? 'active' : ''}`}
                    onClick={() => setCampaignPipelineTab('monitoring')}
                  >
                    <span className="tab-icon">📊</span>
                    <span className="tab-text">Monitoring</span>
                    <span className="tab-badge">{performanceData?.monitoring?.length || campaigns.published?.length || 0}</span>
                  </button>
                  <button 
                    className={`pipeline-tab-btn ${campaignPipelineTab === 'completed' ? 'active' : ''}`}
                    onClick={() => setCampaignPipelineTab('completed')}
                  >
                    <span className="tab-icon">🎉</span>
                    <span className="tab-text">Completed</span>
                    <span className="tab-badge">{campaigns.completed?.length || 0}</span>
                  </button>
                  <button 
                    className={`pipeline-tab-btn ${campaignPipelineTab === 'issues' ? 'active' : ''}`}
                    onClick={() => setCampaignPipelineTab('issues')}
                  >
                    <span className="tab-icon">❌</span>
                    <span className="tab-text">Issues</span>
                    <span className="tab-badge">{(campaigns.rejected?.length || 0) + (campaigns.cancelled?.length || 0)}</span>
                  </button>
                </div>
                
                {/* Pipeline Content */}
                <div className="pipeline-content-creator">
                  
                  {/* Active Tab */}
                  {campaignPipelineTab === 'active' && (
                    <div className="pipeline-tab-content">
                      {campaigns.active?.filter(c => c.task_status !== 'submitted').length === 0 ? (
                        <div className="empty-state-creator">
                          <span className="empty-icon">📋</span>
                          <h4>No active campaigns</h4>
                          <p>Accept campaign invitations to see them here</p>
                        </div>
                      ) : (
                        <div className="campaign-cards-grid">
                          {campaigns.active?.filter(c => c.task_status !== 'submitted').map(campaign => {
                            const dueDate = campaign.task?.due_date ? new Date(campaign.task.due_date) : null;
                            const now = new Date();
                            const daysLeft = dueDate ? Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24)) : null;
                            const urgencyClass = daysLeft !== null ? (daysLeft <= 1 ? 'urgent' : daysLeft <= 3 ? 'warning' : 'normal') : 'normal';
                            
                            return (
                              <div key={campaign.id} className={`campaign-card-redesigned ${campaign.task_status}`}>
                                <div className="card-header">
                                  <div className="campaign-title-area">
                                    <h4>{campaign.campaign_name || campaign.name}</h4>
                                    <span className="brand-tag">by {campaign.brand_name}</span>
                                  </div>
                                  <div className="payment-badge">
                                    <span className="currency">₹</span>
                                    <span className="amount">{(campaign.base_payment || 0).toLocaleString()}</span>
                                  </div>
                                </div>
                                
                                {/* Status & Deadline Bar */}
                                <div className="status-deadline-bar">
                                  <span className={`status-pill ${campaign.task_status}`}>
                                    {campaign.task_status === 'assigned' && '📝 Ready to Submit'}
                                    {campaign.task_status === 'revision_needed' && '🔄 Revision Needed'}
                                    {campaign.task_status === 'approved' && '✅ Approved'}
                                  </span>
                                  {dueDate && (
                                    <span className={`deadline-pill ${urgencyClass}`}>
                                      ⏰ {daysLeft === 0 ? 'Due Today!' : daysLeft === 1 ? '1 day left' : `${daysLeft} days left`}
                                    </span>
                                  )}
                                </div>
                                
                                {/* Submission Progress */}
                                {campaign.task?.submission_count > 0 && (
                                  <div className="submission-progress">
                                    <div className="progress-text">
                                      <span>Submissions</span>
                                      <span>{campaign.task.submission_count}/{campaign.task.max_submissions || 3}</span>
                                    </div>
                                    <div className="progress-bar-sm">
                                      <div 
                                        className="progress-fill-sm" 
                                        style={{width: `${(campaign.task.submission_count / (campaign.task.max_submissions || 3)) * 100}%`}}
                                      ></div>
                                    </div>
                                  </div>
                                )}
                                
                                {/* Brand Feedback */}
                                {campaign.task_status === 'revision_needed' && campaign.task?.feedback && (
                                  <div className="brand-feedback-box">
                                    <strong>📣 Brand Feedback:</strong>
                                    <p>{campaign.task.feedback}</p>
                                  </div>
                                )}
                                
                                {/* Content Brief Collapsible */}
                                {(campaign.content_brief || campaign.task?.content_brief) && (
                                  <details className="brief-collapsible">
                                    <summary>📋 View Content Brief</summary>
                                    <div className="brief-content">
                                      <p>
                                        {typeof campaign.content_brief === 'string' 
                                          ? campaign.content_brief
                                          : (campaign.task?.content_brief || campaign.content_brief?.brief || 'No brief provided')}
                                      </p>
                                      {(campaign.hashtags?.length > 0 || campaign.task?.hashtags?.length > 0) && (
                                        <div className="hashtags-list">
                                          {(campaign.hashtags || campaign.task?.hashtags)?.map((tag, i) => (
                                            <span key={i} className="hashtag-chip">{tag}</span>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </details>
                                )}
                                
                                {/* Action Button */}
                                <div className="card-actions">
                                  {(campaign.task_status === 'assigned' || campaign.task_status === 'revision_needed') && (
                                    <button 
                                      className="btn-primary-action"
                                      onClick={() => {
                                        setSelectedCampaignForSubmission(campaign);
                                        setShowSubmissionModal(true);
                                      }}
                                    >
                                      {campaign.task_status === 'revision_needed' 
                                        ? '🔄 Resubmit Content' 
                                        : '📤 Submit Content'}
                                    </button>
                                  )}
                                  {campaign.task_status === 'approved' && (
                                    <button 
                                      className="btn-publish-action"
                                      onClick={() => {
                                        setSelectedCampaignForSubmission(campaign);
                                        setShowPublishModal(true);
                                      }}
                                    >
                                      🚀 Submit Published URL
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Under Review Tab */}
                  {campaignPipelineTab === 'review' && (
                    <div className="pipeline-tab-content">
                      {campaigns.active?.filter(c => c.task_status === 'submitted').length === 0 ? (
                        <div className="empty-state-creator">
                          <span className="empty-icon">⏳</span>
                          <h4>No content under review</h4>
                          <p>Submit content to see it here while brands review</p>
                        </div>
                      ) : (
                        <div className="campaign-cards-grid">
                          {campaigns.active?.filter(c => c.task_status === 'submitted').map(campaign => (
                            <div key={campaign.id} className="campaign-card-redesigned under-review">
                              <div className="card-header">
                                <div className="campaign-title-area">
                                  <h4>{campaign.campaign_name || campaign.name}</h4>
                                  <span className="brand-tag">by {campaign.brand_name}</span>
                                </div>
                                <div className="payment-badge">
                                  <span className="currency">₹</span>
                                  <span className="amount">{(campaign.base_payment || 0).toLocaleString()}</span>
                                </div>
                              </div>
                              
                              <div className="review-status-box">
                                <div className="review-animation">
                                  <span className="pulse-dot"></span>
                                  <span className="review-text">Under Review</span>
                                </div>
                                <p className="review-note">Brand is reviewing your submission. You'll be notified when reviewed.</p>
                              </div>
                              
                              <div className="submission-info">
                                <span>📝 Submission {campaign.task?.submission_count || 1} of {campaign.task?.max_submissions || 3}</span>
                                {campaign.task?.submitted_at && (
                                  <span className="submitted-time">
                                    Submitted: {new Date(campaign.task.submitted_at).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Approved / Ready to Publish Tab */}
                  {campaignPipelineTab === 'approved' && (
                    <div className="pipeline-tab-content">
                      {campaigns.approved?.length === 0 ? (
                        <div className="empty-state-creator">
                          <span className="empty-icon">✅</span>
                          <h4>No approved content</h4>
                          <p>Approved campaigns will appear here for you to publish</p>
                        </div>
                      ) : (
                        <div className="campaign-cards-grid">
                          {campaigns.approved?.map(campaign => (
                            <div key={campaign.id} className="campaign-card-redesigned approved-card">
                              <div className="approved-banner">
                                <span>🎉 Content Approved!</span>
                              </div>
                              <div className="card-header">
                                <div className="campaign-title-area">
                                  <h4>{campaign.campaign_name || campaign.name}</h4>
                                  <span className="brand-tag">by {campaign.brand_name}</span>
                                </div>
                                <div className="payment-badge success">
                                  <span className="currency">₹</span>
                                  <span className="amount">{(campaign.base_payment || 0).toLocaleString()}</span>
                                </div>
                              </div>
                              
                              <div className="next-steps-box">
                                <h5>📋 Next Steps:</h5>
                                <ol>
                                  <li>Post the content on your Instagram</li>
                                  <li>Copy the post URL</li>
                                  <li>Submit the URL below to start monitoring</li>
                                </ol>
                              </div>
                              
                              <div className="card-actions">
                                <button 
                                  className="btn-publish-action large"
                                  onClick={() => {
                                    setSelectedCampaignForSubmission(campaign);
                                    setShowPublishModal(true);
                                  }}
                                >
                                  🚀 Submit Instagram Post URL
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Monitoring Tab */}
                  {campaignPipelineTab === 'monitoring' && (
                    <div className="pipeline-tab-content">
                      {(performanceData?.monitoring?.length || campaigns.published?.length || 0) === 0 ? (
                        <div className="empty-state-creator">
                          <span className="empty-icon">📊</span>
                          <h4>No posts being monitored</h4>
                          <p>After you submit a published URL, performance monitoring begins</p>
                        </div>
                      ) : (
                        <div className="monitoring-cards-grid">
                          {performanceData?.monitoring?.map(perf => {
                            // Cross-reference the campaigns list to pick up the
                            // per-installment payment timeline (only available
                            // on the campaigns endpoint).
                            const linkedCampaign =
                              (campaigns.published || []).find(c => c.id === perf.campaign_id)
                              || (campaigns.completed || []).find(c => c.id === perf.campaign_id);
                            const weeklyPayments = linkedCampaign?.weekly_payments || [];
                            const perfBonus = linkedCampaign?.performance_bonus_payment;
                            const paymentSummary = linkedCampaign?.payment_summary;
                            return (
                            <div key={perf.id} className="monitoring-card-creator">
                              <div className="monitoring-header">
                                <div className="campaign-info">
                                  <h4>{perf.campaign_name}</h4>
                                  <span className="brand-tag">by {perf.brand_name}</span>
                                </div>
                                <div className="monitoring-badge-area">
                                  <span className="day-badge">Day {perf.days_completed || 0}/7</span>
                                  <div className="monitoring-progress-ring">
                                    <svg viewBox="0 0 36 36">
                                      <path
                                        className="circle-bg"
                                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                      />
                                      <path
                                        className="circle-fill"
                                        strokeDasharray={`${((perf.days_completed || 0) / 7) * 100}, 100`}
                                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                      />
                                    </svg>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Instagram Post Link */}
                              {perf.platform_url && (
                                <a 
                                  href={perf.platform_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="instagram-post-link-creator"
                                >
                                  📱 View Instagram Post →
                                </a>
                              )}
                              
                              {/* Live Metrics Grid */}
                              <div className="live-metrics-grid">
                                <div className="metric-box">
                                  <span className="metric-icon">❤️</span>
                                  <span className="metric-value">{(perf.current_metrics?.likes || 0).toLocaleString()}</span>
                                  <span className="metric-label">Likes</span>
                                </div>
                                <div className="metric-box">
                                  <span className="metric-icon">💬</span>
                                  <span className="metric-value">{(perf.current_metrics?.comments || 0).toLocaleString()}</span>
                                  <span className="metric-label">Comments</span>
                                </div>
                                <div className="metric-box">
                                  <span className="metric-icon">👁️</span>
                                  <span className="metric-value">{(perf.current_metrics?.views || 0).toLocaleString()}</span>
                                  <span className="metric-label">Views</span>
                                </div>
                                <div className="metric-box">
                                  <span className="metric-icon">📊</span>
                                  <span className="metric-value">{(perf.current_metrics?.reach || 0).toLocaleString()}</span>
                                  <span className="metric-label">Reach</span>
                                </div>
                              </div>
                              
                              {/* Per-installment payment timeline (admin records payouts) */}
                              {(weeklyPayments.length > 0 || perfBonus) && (
                                <div className="creator-payment-timeline">
                                  <div className="timeline-title">Payment timeline</div>
                                  <p className="timeline-fee-note">
                                    Amounts shown are your net payout after a 3.5% platform fee on each weekly and performance release.
                                  </p>
                                  <div className="timeline-pills">
                                    {weeklyPayments.map(wp => (
                                      <div
                                        key={`w${wp.week_number}`}
                                        className={`creator-payment-pill creator-payment-pill--${wp.status}`}
                                        title={wp.status_label}
                                      >
                                        <div className="pill-head">
                                          Week {wp.week_number}
                                          <span className="pill-amount">{formatCurrency(wp.net_amount)}</span>
                                        </div>
                                        <div className="pill-status">{wp.status_label}</div>
                                        {(wp.platform_fee > 0) && (
                                          <div className="pill-fee-detail">
                                            Gross {formatCurrency(wp.gross_amount)} · Platform fee −{formatCurrency(wp.platform_fee)}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                    {perfBonus && (
                                      <div
                                        className={`creator-payment-pill creator-payment-pill--${perfBonus.status}`}
                                        title={perfBonus.status_label}
                                      >
                                        <div className="pill-head">
                                          Performance bonus
                                          {!perfBonus.is_estimate && (
                                            <span className="pill-amount">{formatCurrency(perfBonus.net_amount)}</span>
                                          )}
                                        </div>
                                        <div className="pill-status">{perfBonus.status_label}</div>
                                        {!perfBonus.is_estimate && (perfBonus.platform_fee > 0) && (
                                          <div className="pill-fee-detail">
                                            Gross {formatCurrency(perfBonus.gross_amount)} · Platform fee −{formatCurrency(perfBonus.platform_fee)}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  {paymentSummary && (
                                    <div className="timeline-summary">
                                      <span><strong>Earned:</strong> {formatCurrency(paymentSummary.total_earned)}</span>
                                      <span><strong>Pending release:</strong> {formatCurrency(paymentSummary.pending_release)}</span>
                                      <span><strong>Upcoming:</strong> {formatCurrency(paymentSummary.upcoming)}</span>
                                    </div>
                                  )}
                                </div>
                              )}

                              <div className="monitoring-footer">
                                <button
                                  className="btn-refresh-metrics"
                                  onClick={() => handleRefreshPerformance(perf.id)}
                                  disabled={refreshingPerformance === perf.id}
                                >
                                  {refreshingPerformance === perf.id ? '🔄' : '🔄 Refresh'}
                                </button>
                              </div>

                              <div className="last-updated-info">
                                Last updated: {perf.last_checked_at ? new Date(perf.last_checked_at).toLocaleString() : 'Not yet'}
                              </div>
                            </div>
                          );
                          })}

                          {/* Fallback for published without performance data */}
                          {(!performanceData?.monitoring || performanceData.monitoring.length === 0) && campaigns.published?.map(campaign => (
                            <div key={campaign.id} className="monitoring-card-creator pending-metrics">
                              <div className="monitoring-header">
                                <div className="campaign-info">
                                  <h4>{campaign.campaign_name || campaign.name}</h4>
                                  <span className="brand-tag">by {campaign.brand_name}</span>
                                </div>
                                <span className="monitoring-badge-pending">📊 Awaiting Metrics</span>
                              </div>
                              <div className="pending-metrics-message">
                                <span>⏳ Monitoring started. Metrics will appear soon...</span>
                              </div>
                              {(campaign.weekly_payments?.length > 0 || campaign.performance_bonus_payment) && (
                                <div className="creator-payment-timeline">
                                  <div className="timeline-title">Payment timeline</div>
                                  <p className="timeline-fee-note">
                                    Amounts shown are your net payout after a 3.5% platform fee on each weekly and performance release.
                                  </p>
                                  <div className="timeline-pills">
                                    {campaign.weekly_payments?.map(wp => (
                                      <div
                                        key={`w${wp.week_number}`}
                                        className={`creator-payment-pill creator-payment-pill--${wp.status}`}
                                        title={wp.status_label}
                                      >
                                        <div className="pill-head">
                                          Week {wp.week_number}
                                          <span className="pill-amount">{formatCurrency(wp.net_amount)}</span>
                                        </div>
                                        <div className="pill-status">{wp.status_label}</div>
                                        {(wp.platform_fee > 0) && (
                                          <div className="pill-fee-detail">
                                            Gross {formatCurrency(wp.gross_amount)} · Platform fee −{formatCurrency(wp.platform_fee)}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                    {campaign.performance_bonus_payment && (
                                      <div
                                        className={`creator-payment-pill creator-payment-pill--${campaign.performance_bonus_payment.status}`}
                                        title={campaign.performance_bonus_payment.status_label}
                                      >
                                        <div className="pill-head">
                                          Performance bonus
                                          {!campaign.performance_bonus_payment.is_estimate && (
                                            <span className="pill-amount">{formatCurrency(campaign.performance_bonus_payment.net_amount)}</span>
                                          )}
                                        </div>
                                        <div className="pill-status">{campaign.performance_bonus_payment.status_label}</div>
                                        {!campaign.performance_bonus_payment.is_estimate && (campaign.performance_bonus_payment.platform_fee > 0) && (
                                          <div className="pill-fee-detail">
                                            Gross {formatCurrency(campaign.performance_bonus_payment.gross_amount)} · Platform fee −{formatCurrency(campaign.performance_bonus_payment.platform_fee)}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Completed Tab */}
                  {campaignPipelineTab === 'completed' && (
                    <div className="pipeline-tab-content">
                      {campaigns.completed?.length === 0 ? (
                        <div className="empty-state-creator">
                          <span className="empty-icon">🎉</span>
                          <h4>No completed campaigns yet</h4>
                          <p>Campaigns move here after successful performance monitoring</p>
                        </div>
                      ) : (
                        <div className="campaign-cards-grid">
                          {campaigns.completed?.map(campaign => {
                            // Prefer the actual paid total over the gross base_payment estimate.
                            const earned = campaign.payment_summary?.total_earned ?? campaign.base_payment ?? 0;
                            const pendingRelease = campaign.payment_summary?.pending_release || 0;
                            return (
                            <div key={campaign.id} className="campaign-card-redesigned completed-card">
                              <div className="completed-banner">
                                <span>🎉 Campaign Completed</span>
                              </div>
                              <div className="card-header">
                                <div className="campaign-title-area">
                                  <h4>{campaign.campaign_name || campaign.name}</h4>
                                  <span className="brand-tag">by {campaign.brand_name}</span>
                                </div>
                              </div>
                              <div className="completed-earnings">
                                <span className="earnings-label">Earned</span>
                                <span className="earnings-value">{formatCurrency(earned)}</span>
                                {pendingRelease > 0 && (
                                  <span className="earnings-pending-note">
                                    + {formatCurrency(pendingRelease)} pending Milan AI release
                                  </span>
                                )}
                              </div>

                              {/* Per-installment breakdown so the creator can
                                  see exactly which weeks/bonus were paid. */}
                              {(campaign.weekly_payments?.length > 0 || campaign.performance_bonus_payment) && (
                                <div className="creator-payment-timeline">
                                  <p className="timeline-fee-note">
                                    Amounts shown are your net payout after a 3.5% platform fee on each weekly and performance release.
                                  </p>
                                  <div className="timeline-pills">
                                    {campaign.weekly_payments?.map(wp => (
                                      <div
                                        key={`w${wp.week_number}`}
                                        className={`creator-payment-pill creator-payment-pill--${wp.status}`}
                                        title={wp.status_label}
                                      >
                                        <div className="pill-head">
                                          Week {wp.week_number}
                                          <span className="pill-amount">{formatCurrency(wp.net_amount)}</span>
                                        </div>
                                        <div className="pill-status">{wp.status_label}</div>
                                        {(wp.platform_fee > 0) && (
                                          <div className="pill-fee-detail">
                                            Gross {formatCurrency(wp.gross_amount)} · Platform fee −{formatCurrency(wp.platform_fee)}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                    {campaign.performance_bonus_payment && (
                                      <div
                                        className={`creator-payment-pill creator-payment-pill--${campaign.performance_bonus_payment.status}`}
                                        title={campaign.performance_bonus_payment.status_label}
                                      >
                                        <div className="pill-head">
                                          Performance bonus
                                          {!campaign.performance_bonus_payment.is_estimate && (
                                            <span className="pill-amount">{formatCurrency(campaign.performance_bonus_payment.net_amount)}</span>
                                          )}
                                        </div>
                                        <div className="pill-status">{campaign.performance_bonus_payment.status_label}</div>
                                        {!campaign.performance_bonus_payment.is_estimate && (campaign.performance_bonus_payment.platform_fee > 0) && (
                                          <div className="pill-fee-detail">
                                            Gross {formatCurrency(campaign.performance_bonus_payment.gross_amount)} · Platform fee −{formatCurrency(campaign.performance_bonus_payment.platform_fee)}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {campaign.completed_at && (
                                <div className="completed-date">
                                  Completed: {new Date(campaign.completed_at).toLocaleDateString()}
                                </div>
                              )}
                            </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Issues Tab (Rejected + Cancelled) */}
                  {campaignPipelineTab === 'issues' && (
                    <div className="pipeline-tab-content">
                      {(campaigns.rejected?.length || 0) + (campaigns.cancelled?.length || 0) === 0 ? (
                        <div className="empty-state-creator">
                          <span className="empty-icon">✨</span>
                          <h4>No issues!</h4>
                          <p>All your campaigns are in good standing</p>
                        </div>
                      ) : (
                        <div className="issues-content">
                          {/* Rejected Section */}
                          {campaigns.rejected?.length > 0 && (
                            <div className="issues-section rejected">
                              <h4>❌ Rejected Campaigns</h4>
                              <p className="section-note">Content rejected after all submission attempts</p>
                              <div className="campaign-cards-grid">
                                {campaigns.rejected?.map(campaign => (
                                  <div key={campaign.id} className="campaign-card-redesigned rejected-card">
                                    <div className="card-header">
                                      <div className="campaign-title-area">
                                        <h4>{campaign.campaign_name || campaign.name}</h4>
                                        <span className="brand-tag">by {campaign.brand_name}</span>
                                      </div>
                                      <span className="issue-badge rejected">❌ Rejected</span>
                                    </div>
                                    {campaign.task?.feedback && (
                                      <div className="feedback-box">
                                        <strong>Feedback:</strong>
                                        <p>{campaign.task.feedback}</p>
                                      </div>
                                    )}
                                    <div className="issue-meta">
                                      <span>📝 Submissions: {campaign.task?.submission_count || 0}/{campaign.task?.max_submissions || 3}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Cancelled Section */}
                          {campaigns.cancelled?.length > 0 && (
                            <div className="issues-section cancelled">
                              <h4>🚫 Cancelled Campaigns</h4>
                              <p className="section-note">Brand cancelled after content submission. You're entitled to a 5% kill fee of the fixed payment.</p>
                              <div className="campaign-cards-grid">
                                {campaigns.cancelled?.map(campaign => (
                                  <div key={campaign.id} className="campaign-card-redesigned cancelled-card">
                                    <div className="card-header">
                                      <div className="campaign-title-area">
                                        <h4>{campaign.campaign_name || campaign.name}</h4>
                                        <span className="brand-tag">by {campaign.brand_name}</span>
                                      </div>
                                      <span className="issue-badge cancelled">🚫 Cancelled</span>
                                    </div>
                                    {campaign.cancellation_reason && (
                                      <div className="feedback-box">
                                        <strong>Reason:</strong>
                                        <p>{campaign.cancellation_reason}</p>
                                      </div>
                                    )}
                                    <div className="kill-fee-info">
                                      <div className="kill-fee-row">
                                        <span>Original Payment:</span>
                                        <span className="strikethrough">{formatCurrency(campaign.fixed_payment || 0)}</span>
                                      </div>
                                      <div className="kill-fee-row highlight">
                                        <span>💰 Kill Fee (5%):</span>
                                        <span className="kill-fee-amount">{formatCurrency(campaign.kill_fee || 0)}</span>
                                      </div>
                                      {(() => {
                                        // Backend exposes campaign.kill_fee_status
                                        // ('pending'|'processing'|'completed'|'none')
                                        // sourced from the underlying creator_payments
                                        // row. Honour it instead of hard-coding
                                        // "Payment Pending" so settled kill fees
                                        // visibly clear from the Issues tab.
                                        const kfs = (campaign.kill_fee_status || 'pending').toLowerCase();
                                        if (kfs === 'completed' || kfs === 'paid') {
                                          return <span className="kill-fee-status paid">✅ Paid by Milan AI</span>;
                                        }
                                        if (kfs === 'processing') {
                                          return <span className="kill-fee-status processing">🔄 Processing</span>;
                                        }
                                        if (kfs === 'failed') {
                                          return <span className="kill-fee-status failed">❌ Payment Failed</span>;
                                        }
                                        return <span className="kill-fee-status">⏳ Payment Pending</span>;
                                      })()}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <div className="tab-content analytics-page">
              <h1>Analytics</h1>
              <p className="tab-description">Track your content performance and profile metrics</p>

              {/* Campaign Stats Summary */}
              <div className="analytics-summary-cards">
                <div className="analytics-stat-card">
                  <span className="stat-icon">🎯</span>
                  <div className="stat-info">
                    <span className="stat-value">{campaigns.completed?.length || 0}</span>
                    <span className="stat-label">Completed Campaigns</span>
                  </div>
                </div>
                <div className="analytics-stat-card">
                  <span className="stat-icon">⭐</span>
                  <div className="stat-info">
                    <span className="stat-value">{campaigns.active?.length || 0}</span>
                    <span className="stat-label">Active Campaigns</span>
                  </div>
                </div>
                <div className="analytics-stat-card">
                  <span className="stat-icon">📱</span>
                  <div className="stat-info">
                    <span className="stat-value">{performanceData?.monitoring?.length || 0}</span>
                    <span className="stat-label">Posts Monitored</span>
                  </div>
                </div>
                <div className="analytics-stat-card">
                  <span className="stat-icon">👥</span>
                  <div className="stat-info">
                    <span className="stat-value">{formatNumber(analytics?.overview?.total_followers || instagramProfile?.followers_count || 0)}</span>
                    <span className="stat-label">Followers</span>
                  </div>
                </div>
              </div>

              {/* Real-Time Campaign Performance - From Submitted URLs */}
              <div className="analytics-section">
                <div className="analytics-section-header">
                  <h3>📊 Real-Time Campaign Performance</h3>
                  {(performanceData?.monitoring?.length || 0) > 0 && (
                    <button 
                      className="btn-refresh-all"
                      onClick={handleRefreshAllPerformance}
                      disabled={refreshingPerformance === 'all'}
                    >
                      {refreshingPerformance === 'all' ? '🔄 Refreshing...' : '🔄 Refresh All'}
                    </button>
                  )}
                </div>
                {(performanceData?.monitoring?.length || 0) > 0 ? (
                  <>
                    <div className="real-performance-notice">
                      <span className="live-dot"></span>
                      <span>Live metrics from {performanceData.monitoring.length} monitored post(s)</span>
                    </div>
                    <div className="analytics-quick-grid">
                      <div className="analytics-quick-card highlight">
                        <span className="quick-icon">❤️</span>
                        <span className="quick-value">
                          {formatNumber((performanceData.monitoring || []).reduce((sum, p) => sum + (p.current_metrics?.likes || 0), 0))}
                        </span>
                        <span className="quick-label">Total Likes</span>
                      </div>
                      <div className="analytics-quick-card highlight">
                        <span className="quick-icon">💬</span>
                        <span className="quick-value">
                          {formatNumber((performanceData.monitoring || []).reduce((sum, p) => sum + (p.current_metrics?.comments || 0), 0))}
                        </span>
                        <span className="quick-label">Total Comments</span>
                      </div>
                      <div className="analytics-quick-card highlight">
                        <span className="quick-icon">👁️</span>
                        <span className="quick-value">
                          {formatNumber((performanceData.monitoring || []).reduce((sum, p) => sum + (p.current_metrics?.views || 0), 0))}
                        </span>
                        <span className="quick-label">Total Views</span>
                      </div>
                      <div className="analytics-quick-card highlight">
                        <span className="quick-icon">📊</span>
                        <span className="quick-value">
                          {formatNumber((performanceData.monitoring || []).reduce((sum, p) => sum + (p.current_metrics?.reach || 0), 0))}
                        </span>
                        <span className="quick-label">Total Reach</span>
                      </div>
                      <div className="analytics-quick-card">
                        <span className="quick-icon">🔖</span>
                        <span className="quick-value">
                          {formatNumber((performanceData.monitoring || []).reduce((sum, p) => sum + (p.current_metrics?.saves || 0), 0))}
                        </span>
                        <span className="quick-label">Total Saves</span>
                      </div>
                      <div className="analytics-quick-card">
                        <span className="quick-icon">📱</span>
                        <span className="quick-value">{performanceData.monitoring.length}</span>
                        <span className="quick-label">Posts Tracked</span>
                      </div>
                    </div>
                    
                    {/* Individual Post Performance */}
                    <div className="post-performance-list">
                      <h4>📱 Individual Post Performance</h4>
                      {performanceData.monitoring.map(perf => (
                        <div key={perf.id} className="post-performance-item">
                          <div className="post-info">
                            <span className="post-campaign">{perf.campaign_name}</span>
                            <span className="post-brand">by {perf.brand_name}</span>
                            <span className="post-day">Day {perf.days_completed || 0}/7</span>
                          </div>
                          <div className="post-metrics-mini">
                            <span>❤️ {(perf.current_metrics?.likes || 0).toLocaleString()}</span>
                            <span>💬 {(perf.current_metrics?.comments || 0).toLocaleString()}</span>
                            <span>👁️ {(perf.current_metrics?.views || 0).toLocaleString()}</span>
                          </div>
                          {perf.platform_url && (
                            <a href={perf.platform_url} target="_blank" rel="noopener noreferrer" className="post-link">
                              View Post →
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="no-performance-data">
                    <span className="no-data-icon">📊</span>
                    <h4>No Posts Being Monitored</h4>
                    <p>When you submit published Instagram URLs, real-time performance metrics will appear here</p>
                  </div>
                )}
              </div>

              {/* Profile Analytics (if connected to Instagram) */}
              <div className="analytics-section">
                <h3>👤 Profile Analytics</h3>
                <div className="analytics-quick-grid">
                  <div className="analytics-quick-card">
                    <span className="quick-icon">👥</span>
                    <span className="quick-value">{formatNumber(analytics?.overview?.total_followers || instagramProfile?.followers_count || 0)}</span>
                    <span className="quick-label">Followers</span>
                  </div>
                  <div className="analytics-quick-card">
                    <span className="quick-icon">📈</span>
                    <span className="quick-value">{analytics?.overview?.avg_engagement_rate || 0}%</span>
                    <span className="quick-label">Engagement Rate</span>
                  </div>
                  <div className="analytics-quick-card">
                    <span className="quick-icon">🎯</span>
                    <span className="quick-value">{campaigns.completed?.length || 0}</span>
                    <span className="quick-label">Campaigns Done</span>
                  </div>
                  <div className="analytics-quick-card">
                    <span className="quick-icon">⭐</span>
                    <span className="quick-value">{campaigns.active?.length || 0}</span>
                    <span className="quick-label">Active Campaigns</span>
                  </div>
                </div>
              </div>

            </div>
          )}

          {/* Payments Tab - Payment History */}
          {activeTab === 'payments' && (
            <div className="tab-content payments-page">
              <h1>Payments</h1>
              <p className="tab-description">Track all your earnings and campaign payments</p>

              {/* ---------- Hero: Next Payout ----------
                  Surfaces the most useful single fact: when is my next
                  installment, what is it, and what's the amount. Skipped
                  when the creator has nothing pending or upcoming. */}
              {(() => {
                const allInvoices = invoices.all || [];
                if (allInvoices.length === 0) return null;
                // Next = first matured-unpaid; otherwise the soonest upcoming
                const matured = allInvoices
                  .filter(i => i.status === 'sent')
                  .sort((a, b) => new Date(a.matures_at || a.created_at) - new Date(b.matures_at || b.created_at));
                const upcomingSoon = allInvoices
                  .filter(i => i.status === 'approved' && (i.amount || 0) > 0)
                  .sort((a, b) => new Date(a.matures_at) - new Date(b.matures_at));
                const next = matured[0] || upcomingSoon[0];
                if (!next) return null;
                const isMatured = next.status === 'sent';
                const matureDate = next.matures_at ? new Date(next.matures_at) : null;
                const daysAway = matureDate ? Math.max(0, Math.ceil((matureDate - new Date()) / 86400000)) : 0;
                return (
                  <div className={`next-payout-hero ${isMatured ? 'is-matured' : 'is-upcoming'}`}>
                    <div className="next-payout-icon">{isMatured ? '⏳' : '🗓️'}</div>
                    <div className="next-payout-body">
                      <span className="next-payout-eyebrow">
                        {isMatured ? 'Awaiting Milan AI release' : 'Next milestone'}
                      </span>
                      <h3 className="next-payout-title">
                        {next.label} · {next.campaign_name}
                      </h3>
                      <span className="next-payout-meta">
                        {isMatured
                          ? 'Matured · The Milan AI team will release this shortly'
                          : daysAway === 0
                            ? `Matures today · ${matureDate?.toLocaleDateString()}`
                            : `Matures in ${daysAway} day${daysAway === 1 ? '' : 's'} · ${matureDate?.toLocaleDateString()}`}
                      </span>
                    </div>
                    <div className="next-payout-amount">
                      <span className="amount">{formatCurrency(next.amount)}</span>
                      <span className="amount-sub">net to you</span>
                    </div>
                  </div>
                );
              })()}

              {/* Earnings Summary Cards — sourced from per-installment ledger */}
              {(() => {
                const summaryFallback = (campaigns.all || []).reduce(
                  (acc, c) => {
                    const s = c.payment_summary;
                    if (!s) return acc;
                    acc.earned += s.total_earned || 0;
                    acc.pending += s.pending_release || 0;
                    acc.upcoming += s.upcoming || 0;
                    return acc;
                  },
                  { earned: 0, pending: 0, upcoming: 0 }
                );

                const totalEarned = paymentEarnings?.total_earned ?? summaryFallback.earned;
                const pendingRelease = paymentEarnings?.pending_amount ?? summaryFallback.pending;
                const upcomingPayouts = paymentEarnings?.upcoming_amount ?? summaryFallback.upcoming;
                const killFeesPending = (campaigns.cancelled || []).reduce(
                  (sum, c) => sum + (c.kill_fee || 0),
                  0
                );

                // "Released this month" — this calendar month's bucket from
                // the monthly_breakdown (real, not mock).
                const thisMonthLabel = new Date().toLocaleString('en-US', { month: 'short' });
                const releasedThisMonth = (earnings?.monthly_breakdown || []).find(
                  m => m.month === thisMonthLabel
                )?.amount || 0;

                return (
                  <div className="earnings-overview-cards">
                    <div className="earnings-card total">
                      <span className="earnings-icon">💰</span>
                      <div className="earnings-info">
                        <span className="earnings-value">{formatCurrency(totalEarned)}</span>
                        <span className="earnings-label">Paid by Milan AI</span>
                      </div>
                    </div>
                    <div className="earnings-card pending">
                      <span className="earnings-icon">⏳</span>
                      <div className="earnings-info">
                        <span className="earnings-value">{formatCurrency(pendingRelease)}</span>
                        <span className="earnings-label">Pending release</span>
                      </div>
                    </div>
                    <div className="earnings-card upcoming">
                      <span className="earnings-icon">🚀</span>
                      <div className="earnings-info">
                        <span className="earnings-value">{formatCurrency(upcomingPayouts)}</span>
                        <span className="earnings-label">Upcoming weeks</span>
                      </div>
                    </div>
                    {killFeesPending > 0 ? (
                      <div className="earnings-card available">
                        <span className="earnings-icon">🛑</span>
                        <div className="earnings-info">
                          <span className="earnings-value">{formatCurrency(killFeesPending)}</span>
                          <span className="earnings-label">Kill fees</span>
                        </div>
                      </div>
                    ) : (
                      <div className="earnings-card month">
                        <span className="earnings-icon">📅</span>
                        <div className="earnings-info">
                          <span className="earnings-value">{formatCurrency(releasedThisMonth)}</span>
                          <span className="earnings-label">Released this month</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Monthly Earnings Chart — hide when nothing has been paid yet
                  so we don't show an empty 6-month bar chart for new
                  creators. */}
              {(() => {
                const breakdown = earnings?.monthly_breakdown || [];
                const total = breakdown.reduce((s, m) => s + (m.amount || 0), 0);
                if (total === 0) return null;
                const maxAmount = Math.max(...breakdown.map(m => m.amount || 0), 1);
                const bestMonth = breakdown.reduce(
                  (best, m) => ((m.amount || 0) > (best?.amount || 0) ? m : best),
                  breakdown[0]
                );
                const avgAmount = total / breakdown.length;
                const lastIdx = breakdown.length - 1;
                // Build a smooth area-chart polyline path under the bars
                const chartW = 100; // percent
                const chartH = 100; // percent
                const stepX = breakdown.length > 1 ? chartW / (breakdown.length - 1) : 0;
                const points = breakdown.map((m, i) => {
                  const x = i * stepX;
                  const y = chartH - ((m.amount || 0) / maxAmount) * chartH;
                  return `${x},${y}`;
                });
                const linePath = points.join(' ');
                const areaPath = `0,${chartH} ${linePath} ${chartW},${chartH}`;
                return (
                  <div className="payments-section-card monthly-earnings-card">
                    <div className="me-header">
                      <div className="me-title-block">
                        <h3>Monthly Earnings</h3>
                        <p className="me-subtitle">
                          Last 6 months · {formatCurrency(total)} earned
                        </p>
                      </div>
                      <div className="me-stats">
                        <div className="me-stat">
                          <span className="me-stat-label">Best month</span>
                          <span className="me-stat-value">
                            {bestMonth?.month || '—'} · {formatCurrency(bestMonth?.amount || 0)}
                          </span>
                        </div>
                        <div className="me-stat">
                          <span className="me-stat-label">Avg / month</span>
                          <span className="me-stat-value">{formatCurrency(avgAmount)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="me-chart">
                      <div className="me-yaxis">
                        <span>{formatCurrency(maxAmount)}</span>
                        <span>{formatCurrency(maxAmount / 2)}</span>
                        <span>₹0</span>
                      </div>
                      <div className="me-plot">
                        <div className="me-gridlines">
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                        <svg
                          className="me-area"
                          viewBox={`0 0 ${chartW} ${chartH}`}
                          preserveAspectRatio="none"
                          aria-hidden="true"
                        >
                          <defs>
                            <linearGradient id="meAreaFill" x1="0" x2="0" y1="0" y2="1">
                              <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.35" />
                              <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0" />
                            </linearGradient>
                          </defs>
                          <polygon points={areaPath} fill="url(#meAreaFill)" />
                          <polyline
                            points={linePath}
                            fill="none"
                            stroke="#8b5cf6"
                            strokeWidth="0.6"
                            strokeLinejoin="round"
                            strokeLinecap="round"
                            vectorEffect="non-scaling-stroke"
                            style={{ strokeWidth: 2 }}
                          />
                        </svg>
                        <div className="me-bars">
                          {breakdown.map((month, idx) => {
                            const heightPct = maxAmount > 0
                              ? (month.amount / maxAmount) * 100
                              : 0;
                            const isCurrent = idx === lastIdx;
                            const hasValue = (month.amount || 0) > 0;
                            return (
                              <div
                                key={idx}
                                className={`me-bar-col${isCurrent ? ' is-current' : ''}${hasValue ? ' has-value' : ' is-empty'}`}
                              >
                                <div className="me-bar-track">
                                  {hasValue && (
                                    <span className="me-bar-amount">
                                      {formatCurrency(month.amount)}
                                    </span>
                                  )}
                                  <div
                                    className="me-bar-fill"
                                    style={{
                                      height: hasValue
                                        ? `${Math.max(heightPct, 6)}%`
                                        : '4px',
                                    }}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    <div className="me-xaxis">
                      {breakdown.map((month, idx) => (
                        <span
                          key={idx}
                          className={`me-month${idx === lastIdx ? ' is-current' : ''}`}
                        >
                          {month.month}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Payment Filter Tabs — counts come from the per-installment
                  invoice payload. We hide "Failed" until there's actually a
                  failed payout to show, and add an "All" view for full
                  campaign-grouped history. */}
              <div className="payment-filter-tabs">
                <button
                  className={`filter-tab ${paymentStatusFilter === 'pending' ? 'active' : ''}`}
                  onClick={() => setPaymentStatusFilter('pending')}
                >
                  ⏳ Pending ({(invoices.sent?.length || 0) + (invoices.approved?.length || 0)})
                </button>
                <button
                  className={`filter-tab ${paymentStatusFilter === 'completed' ? 'active' : ''}`}
                  onClick={() => setPaymentStatusFilter('completed')}
                >
                  ✅ Completed ({invoices.paid?.length || 0})
                </button>
                <button
                  className={`filter-tab ${paymentStatusFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setPaymentStatusFilter('all')}
                >
                  📋 All ({invoices.all?.length || 0})
                </button>
                {(invoices.failed?.length || 0) > 0 && (
                  <button
                    className={`filter-tab ${paymentStatusFilter === 'failed' ? 'active' : ''}`}
                    onClick={() => setPaymentStatusFilter('failed')}
                  >
                    ❌ Failed ({invoices.failed.length})
                  </button>
                )}
              </div>

              {/* Filtered Payment List */}
              <div className="payments-section-card">
                <h3>
                  {paymentStatusFilter === 'pending' && '⏳ Pending Payments'}
                  {paymentStatusFilter === 'completed' && '✅ Completed Payments'}
                  {paymentStatusFilter === 'failed' && '❌ Failed Payments'}
                  {paymentStatusFilter === 'all' && '📋 All Payments'}
                </h3>

                {(() => {
                  let filteredPayments = [];
                  if (paymentStatusFilter === 'pending') {
                    filteredPayments = [...(invoices.sent || []), ...(invoices.approved || [])];
                  } else if (paymentStatusFilter === 'completed') {
                    filteredPayments = invoices.paid || [];
                  } else if (paymentStatusFilter === 'failed') {
                    filteredPayments = invoices.failed || [];
                  } else if (paymentStatusFilter === 'all') {
                    filteredPayments = invoices.all || [];
                  }

                  if (filteredPayments.length === 0) {
                    return (
                      <div className="empty-state">
                        <span className="empty-icon">
                          {paymentStatusFilter === 'pending' && '✅'}
                          {paymentStatusFilter === 'completed' && '📭'}
                          {paymentStatusFilter === 'failed' && '✅'}
                          {paymentStatusFilter === 'all' && '📭'}
                        </span>
                        <h4>
                          {paymentStatusFilter === 'pending' && 'No pending payments'}
                          {paymentStatusFilter === 'completed' && 'No completed payments yet'}
                          {paymentStatusFilter === 'failed' && 'No failed payments'}
                          {paymentStatusFilter === 'all' && 'No payment activity yet'}
                        </h4>
                        <p>
                          {paymentStatusFilter === 'pending' && 'All your payments are up to date'}
                          {paymentStatusFilter === 'completed' && 'Completed payments will appear here'}
                          {paymentStatusFilter === 'failed' && 'Great! No payment failures'}
                          {paymentStatusFilter === 'all' && "Once you join a campaign, your payment timeline will appear here."}
                        </p>
                      </div>
                    );
                  }

                  // ----- "All" view: group by campaign with progress strip -----
                  if (paymentStatusFilter === 'all') {
                    const grouped = filteredPayments.reduce((acc, inv) => {
                      const key = inv.campaign_id || 'unknown';
                      if (!acc[key]) {
                        acc[key] = {
                          campaign_id: inv.campaign_id,
                          campaign_name: inv.campaign_name,
                          brand_name: inv.brand_name,
                          installments: [],
                        };
                      }
                      acc[key].installments.push(inv);
                      return acc;
                    }, {});

                    return (
                      <div className="payments-grouped">
                        {Object.values(grouped).map(group => {
                          // sort: weeks ascending, perf bonus last
                          const installments = [...group.installments].sort((a, b) => {
                            if (a.type === 'performance_bonus') return 1;
                            if (b.type === 'performance_bonus') return -1;
                            return (a.week_number || 0) - (b.week_number || 0);
                          });
                          const paidCount = installments.filter(i => i.status === 'paid').length;
                          const totalCount = installments.length;
                          const earnedSum = installments
                            .filter(i => i.status === 'paid')
                            .reduce((s, i) => s + (i.amount || 0), 0);
                          const pendingSum = installments
                            .filter(i => i.status === 'sent')
                            .reduce((s, i) => s + (i.amount || 0), 0);
                          const upcomingSum = installments
                            .filter(i => i.status === 'approved')
                            .reduce((s, i) => s + (i.amount || 0), 0);
                          const progressPct = totalCount
                            ? Math.round((paidCount / totalCount) * 100) : 0;
                          return (
                            <div key={group.campaign_id} className="campaign-payment-group">
                              <div className="campaign-payment-group__header">
                                <div className="campaign-payment-group__title">
                                  <h4>{group.campaign_name}</h4>
                                  <span className="campaign-payment-group__brand">{group.brand_name}</span>
                                </div>
                                <div className="campaign-payment-group__progress">
                                  <div className="cpg-progress-bar">
                                    <div
                                      className="cpg-progress-fill"
                                      style={{ width: `${progressPct}%` }}
                                    />
                                  </div>
                                  <span className="cpg-progress-label">
                                    {paidCount}/{totalCount} released · {progressPct}%
                                  </span>
                                </div>
                              </div>
                              <div className="campaign-payment-group__stats">
                                <span><strong>{formatCurrency(earnedSum)}</strong> earned</span>
                                {pendingSum > 0 && (
                                  <span>· <strong>{formatCurrency(pendingSum)}</strong> pending</span>
                                )}
                                {upcomingSum > 0 && (
                                  <span>· <strong>{formatCurrency(upcomingSum)}</strong> upcoming</span>
                                )}
                              </div>
                              <div className="campaign-payment-group__rows">
                                {installments.map(inv => {
                                  const isPerfPending =
                                    inv.type === 'performance_bonus' &&
                                    inv.status === 'approved' &&
                                    (inv.amount || 0) === 0;
                                  const dot =
                                    inv.status === 'paid' ? 'paid' :
                                    inv.status === 'sent' ? 'pending' :
                                    isPerfPending ? 'forecast' : 'upcoming';
                                  const right =
                                    inv.status === 'paid'
                                      ? `Paid ${inv.paid_at ? new Date(inv.paid_at).toLocaleDateString() : ''}`
                                      : inv.status === 'sent'
                                        ? 'Awaiting Milan AI release'
                                        : isPerfPending
                                          ? 'After monitoring ends'
                                          : `Matures ${inv.matures_at ? new Date(inv.matures_at).toLocaleDateString() : 'soon'}`;
                                  return (
                                    <div key={inv.id} className={`cpg-row cpg-row--${dot}`}>
                                      <span className={`cpg-dot cpg-dot--${dot}`} />
                                      <span className="cpg-row__label">{inv.label}</span>
                                      <span className="cpg-row__amount">
                                        {isPerfPending ? '—' : formatCurrency(inv.amount || 0)}
                                      </span>
                                      <span className="cpg-row__date">{right}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  }

                  return (
                    <div className="payments-list">
                      {filteredPayments.map(invoice => {
                        const isPerfPending =
                          invoice.type === 'performance_bonus' &&
                          invoice.status === 'approved' &&
                          (invoice.amount || 0) === 0;
                        const statusCopy =
                          invoice.status === 'paid' ? '✅ Paid by Milan AI' :
                          invoice.status === 'sent' ? '⏳ Pending — Milan AI will release shortly' :
                          isPerfPending ? '🔮 Calculated after monitoring ends' :
                          invoice.status === 'approved' ? `📅 Matures ${invoice.matures_at ? new Date(invoice.matures_at).toLocaleDateString() : 'soon'}` :
                          invoice.status === 'failed' ? '❌ Failed' : '⏳ Pending';
                        return (
                        <div key={invoice.id} className={`payment-card ${invoice.status || 'sent'}`}>
                          <div className="payment-header">
                            <div className="payment-info">
                              <h4 className="payment-campaign">
                                {invoice.campaign_name}
                                {invoice.label && (
                                  <span style={{ fontWeight: 400, fontSize: '0.8em', marginLeft: 8, opacity: 0.7 }}>
                                    · {invoice.label}
                                  </span>
                                )}
                              </h4>
                              <span className="payment-brand">{invoice.brand_name}</span>
                            </div>
                            <div className={`payment-status-badge ${invoice.status || 'sent'}`}>
                              {statusCopy}
                            </div>
                          </div>
                          
                          <div className="payment-details">
                            <div className="detail-row">
                              <span className="label">Amount</span>
                              <span className="value amount">
                                {isPerfPending ? '—' : formatCurrency(invoice.amount || invoice.total_amount || 0)}
                              </span>
                            </div>
                            {invoice.paid_at && (
                              <div className="detail-row">
                                <span className="label">Paid on</span>
                                <span className="value">{new Date(invoice.paid_at).toLocaleDateString()}</span>
                              </div>
                            )}
                            {invoice.matures_at && invoice.status !== 'paid' && (
                              <div className="detail-row">
                                <span className="label">{invoice.status === 'sent' ? 'Matured on' : 'Matures on'}</span>
                                <span className="value">{new Date(invoice.matures_at).toLocaleDateString()}</span>
                              </div>
                            )}
                            {invoice.status === 'paid' && invoice.method && (
                              <div className="detail-row">
                                <span className="label">Method</span>
                                <span className="value">{invoice.method}</span>
                              </div>
                            )}
                          </div>

                          {invoice.status === 'failed' && invoice.failure_reason && (
                            <div className="payment-error-box">
                              <span className="error-icon">⚠️</span>
                              <div className="error-content">
                                <span className="error-label">Failure Reason</span>
                                <span className="error-message">{invoice.failure_reason}</span>
                              </div>
                            </div>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>

              {/* Quick Link to Payment Details */}
              {!paymentDetails && (
                <div className="payment-details-prompt">
                  <span className="prompt-icon">⚠️</span>
                  <div className="prompt-content">
                    <h4>Payment Details Required</h4>
                    <p>Add your UPI or bank details to receive payments</p>
                  </div>
                  <button className="prompt-btn" onClick={() => setActiveTab('payment')}>
                    Add Details →
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Instagram Tab */}
          {activeTab === 'instagram' && (
            <div className="tab-content">
              <h1>Social Platforms</h1>
              
              {/* Platform Cards */}
              <div className="platforms-section">
                <div className="platforms-grid">
                  {/* Instagram - Active */}
                  <div className={`platform-card instagram ${instagramStatus.connected ? 'connected' : ''}`}>
                    <div className="platform-icon instagram-icon">
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                      </svg>
                    </div>
                    <div className="platform-info">
                      <h3>Instagram</h3>
                      {instagramStatus.connected ? (
                        <span className="platform-status connected">
                          <span className="status-dot"></span>
                          Connected as @{instagramStatus.username}
                        </span>
                      ) : (
                        <span className="platform-status not-connected">Not connected</span>
                      )}
                    </div>
                    <span className="platform-badge active">Active</span>
                  </div>

                  {/* YouTube - Coming Soon */}
                  <div className="platform-card youtube coming-soon">
                    <div className="platform-icon youtube-icon">
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                      </svg>
                    </div>
                    <div className="platform-info">
                      <h3>YouTube</h3>
                      <span className="platform-status coming-soon-text">Integration in development</span>
                    </div>
                    <span className="platform-badge coming-soon">Coming Soon</span>
                  </div>

                  {/* Twitter/X - Coming Soon */}
                  <div className="platform-card twitter coming-soon">
                    <div className="platform-icon twitter-icon">
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                    </div>
                    <div className="platform-info">
                      <h3>X (Twitter)</h3>
                      <span className="platform-status coming-soon-text">Integration in development</span>
                    </div>
                    <span className="platform-badge coming-soon">Coming Soon</span>
                  </div>
                </div>
                
                <div className="platforms-note">
                  <span className="note-icon">💡</span>
                  <p>We're currently supporting <strong>Instagram creators</strong>. YouTube and X (Twitter) integrations are coming soon! Stay tuned for updates.</p>
                </div>
              </div>

              <div className="section-divider"></div>
              
              <h2 className="section-title">Instagram Details</h2>
              
              {instagramStatus.connected ? (
                <>
                  {instagramLoading ? (
                    <div className="instagram-loading">
                      <div className="loading-spinner"></div>
                      <p>Loading Instagram data...</p>
                    </div>
                  ) : instagramProfile ? (
                    <div className="instagram-profile-full">
                      {/* Profile Header */}
                      <div className="ig-profile-card">
                        <div className="ig-profile-header-full">
                          <div className="ig-avatar-large">
                            {instagramProfile.profile_picture_url ? (
                              <img src={instagramProfile.profile_picture_url} alt="Profile" />
                            ) : (
                              <span>📸</span>
                            )}
                          </div>
                          <div className="ig-profile-details">
                            <div className="ig-username-row">
                              <h2>@{instagramProfile.username}</h2>
                              <span className="verified-badge">✓ Verified</span>
                            </div>
                            {instagramProfile.name && (
                              <p className="ig-name">{instagramProfile.name}</p>
                            )}
                            {instagramProfile.biography && (
                              <p className="ig-bio">{instagramProfile.biography}</p>
                            )}
                            {instagramProfile.website && (
                              <a href={instagramProfile.website} target="_blank" rel="noopener noreferrer" className="ig-website">
                                {instagramProfile.website}
                              </a>
                            )}
                          </div>
                        </div>

                        {/* Stats Row */}
                        <div className="ig-stats-row">
                          <div className="ig-stat">
                            <span className="ig-stat-value">{formatNumber(instagramProfile.media_count || 0)}</span>
                            <span className="ig-stat-label">Posts</span>
                          </div>
                          <div className="ig-stat">
                            <span className="ig-stat-value">{formatNumber(instagramProfile.followers_count || 0)}</span>
                            <span className="ig-stat-label">Followers</span>
                          </div>
                          <div className="ig-stat">
                            <span className="ig-stat-value">{formatNumber(instagramProfile.following_count || 0)}</span>
                            <span className="ig-stat-label">Following</span>
                          </div>
                          <div className="ig-stat highlight">
                            <span className="ig-stat-value">{instagramProfile.engagement_rate || 0}%</span>
                            <span className="ig-stat-label">Engagement</span>
                          </div>
                        </div>
                      </div>

                      {/* Media Stats */}
                      {instagramProfile.media_stats && (
                        <div className="ig-media-stats">
                          <h3>Content Breakdown</h3>
                          <div className="media-stats-grid">
                            <div className="media-stat-card">
                              <span className="media-stat-icon">🖼️</span>
                              <span className="media-stat-value">{instagramProfile.media_stats.images || 0}</span>
                              <span className="media-stat-label">Images</span>
                            </div>
                            <div className="media-stat-card">
                              <span className="media-stat-icon">🎬</span>
                              <span className="media-stat-value">{instagramProfile.media_stats.videos || 0}</span>
                              <span className="media-stat-label">Videos</span>
                            </div>
                            <div className="media-stat-card">
                              <span className="media-stat-icon">🎥</span>
                              <span className="media-stat-value">{instagramProfile.media_stats.reels || 0}</span>
                              <span className="media-stat-label">Reels</span>
                            </div>
                            <div className="media-stat-card">
                              <span className="media-stat-icon">📚</span>
                              <span className="media-stat-value">{instagramProfile.media_stats.carousels || 0}</span>
                              <span className="media-stat-label">Carousels</span>
                            </div>
                            <div className="media-stat-card">
                              <span className="media-stat-icon">❤️</span>
                              <span className="media-stat-value">{formatNumber(instagramProfile.media_stats.total_likes || 0)}</span>
                              <span className="media-stat-label">Total Likes</span>
                            </div>
                            <div className="media-stat-card">
                              <span className="media-stat-icon">💬</span>
                              <span className="media-stat-value">{formatNumber(instagramProfile.media_stats.total_comments || 0)}</span>
                              <span className="media-stat-label">Total Comments</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Recent Posts */}
                      {instagramProfile.recent_media && instagramProfile.recent_media.length > 0 && (
                        <div className="ig-recent-posts">
                          <h3>Recent Posts</h3>
                          <div className="ig-posts-grid">
                            {instagramProfile.recent_media.map((post, idx) => (
                              <a 
                                key={idx} 
                                href={post.permalink} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="ig-post-card"
                              >
                                <div className="ig-post-image">
                                  {post.media_url || post.thumbnail_url ? (
                                    <img 
                                      src={post.thumbnail_url || post.media_url} 
                                      alt={post.caption || 'Instagram post'} 
                                    />
                                  ) : (
                                    <div className="ig-post-placeholder">
                                      {post.media_type === 'VIDEO' || post.media_type === 'REELS' ? '🎬' : '🖼️'}
                                    </div>
                                  )}
                                  <div className="ig-post-type-badge">
                                    {post.media_type === 'VIDEO' ? '🎬' : 
                                     post.media_type === 'CAROUSEL_ALBUM' ? '📚' : 
                                     post.media_type === 'REELS' ? '🎥' : '🖼️'}
                                  </div>
                                </div>
                                <div className="ig-post-stats">
                                  <span>❤️ {formatNumber(post.like_count || 0)}</span>
                                  <span>💬 {formatNumber(post.comments_count || 0)}</span>
                                </div>
                                {post.caption && (
                                  <p className="ig-post-caption">{post.caption}</p>
                                )}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Account & Media Insights (instagram_business_manage_insights) */}
                      <div className="ig-insights-section">
                        <div className="ig-section-header">
                          <h3>📊 Account Insights</h3>
                          <button
                            className="btn-section-refresh"
                            onClick={fetchInstagramInsights}
                            disabled={instagramInsightsLoading}
                          >
                            {instagramInsightsLoading ? 'Loading…' : '🔄 Refresh'}
                          </button>
                        </div>
                        {instagramInsightsLoading ? (
                          <p className="ig-section-empty">Loading insights…</p>
                        ) : instagramInsights?.insights?.account_insights?.length > 0 ? (
                          <div className="ig-insights-grid">
                            {instagramInsights.insights.account_insights.map((metric, idx) => (
                              <div className="ig-insight-card" key={idx}>
                                <span className="ig-insight-value">
                                  {formatNumber(readInsightValue(metric))}
                                </span>
                                <span className="ig-insight-label">
                                  {metric.title || prettifyMetricName(metric.name)}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="ig-section-empty">
                            No account insights available yet. Insights appear once your
                            professional account has recent activity.
                          </p>
                        )}

                        {instagramInsights?.insights?.media_insights?.length > 0 && (
                          <div className="ig-media-insights">
                            <h4>Recent Post Performance</h4>
                            {instagramInsights.insights.media_insights.map((mi, idx) => (
                              <div className="ig-media-insight-row" key={idx}>
                                <span className="ig-media-insight-type">
                                  {mi.media_type || 'POST'}
                                </span>
                                <div className="ig-media-insight-metrics">
                                  {(mi.insights || []).map((m, j) => (
                                    <span className="ig-media-insight-metric" key={j}>
                                      <strong>{formatNumber(readInsightValue(m))}</strong>{' '}
                                      {prettifyMetricName(m.name)}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Post Comments (instagram_business_manage_comments) */}
                      <div className="ig-comments-section">
                        <div className="ig-section-header">
                          <h3>💬 Recent Comments</h3>
                          <button
                            className="btn-section-refresh"
                            onClick={fetchInstagramComments}
                            disabled={instagramCommentsLoading}
                          >
                            {instagramCommentsLoading ? 'Loading…' : '🔄 Refresh'}
                          </button>
                        </div>
                        {instagramCommentsLoading ? (
                          <p className="ig-section-empty">Loading comments…</p>
                        ) : instagramComments?.posts_with_comments?.length > 0 ? (
                          <div className="ig-comments-list">
                            {instagramComments.posts_with_comments.map((post, idx) => (
                              <div className="ig-comment-post" key={idx}>
                                <div className="ig-comment-post-header">
                                  <span className="ig-comment-post-type">
                                    {post.media_type || 'POST'}
                                  </span>
                                  <span className="ig-comment-post-count">
                                    {formatNumber(post.comments_count || 0)} comments
                                  </span>
                                  {post.permalink && (
                                    <a
                                      href={post.permalink}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="ig-comment-post-link"
                                    >
                                      View post ↗
                                    </a>
                                  )}
                                </div>
                                <div className="ig-comment-items">
                                  {(post.comments || []).map((c, j) => (
                                    <div className="ig-comment-item" key={c.id || j}>
                                      <span className="ig-comment-author">@{c.username || 'user'}</span>
                                      <span className="ig-comment-text">{c.text}</span>
                                      {typeof c.like_count === 'number' && c.like_count > 0 && (
                                        <span className="ig-comment-likes">❤️ {c.like_count}</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="ig-section-empty">
                            No comments found on your recent posts yet.
                          </p>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="ig-profile-actions">
                        <button 
                          className="btn-refresh-profile"
                          onClick={fetchInstagramProfile}
                          disabled={instagramLoading}
                        >
                          {instagramLoading ? 'Refreshing...' : '🔄 Refresh Data'}
                        </button>
                        <button 
                          className="btn-disconnect"
                          onClick={handleInstagramDisconnect}
                        >
                          Disconnect Account
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="instagram-settings-card">
                      <p>Could not load Instagram profile. Please try refreshing.</p>
                      <button 
                        className="btn-refresh"
                        onClick={fetchInstagramProfile}
                      >
                        Retry
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="instagram-settings-card">
                  <div className="instagram-connect-section-dashboard">
                    <div className="connect-illustration">
                      <span className="big-icon">📸</span>
                    </div>
                    <h3>Connect Your Instagram Account</h3>
                    <p className="connect-description">
                      Link your Instagram account to unlock powerful features and boost your visibility on Milan AI.
                    </p>
                    
                    <div className="connect-benefits">
                      <div className="benefit">
                        <span className="benefit-icon">✓</span>
                        <div>
                          <strong>Verify Your Content</strong>
                          <span>Automatically validate your campaign submissions</span>
                        </div>
                      </div>
                      <div className="benefit">
                        <span className="benefit-icon">✓</span>
                        <div>
                          <strong>Showcase Your Stats</strong>
                          <span>Display real follower count and engagement metrics</span>
                        </div>
                      </div>
                      <div className="benefit">
                        <span className="benefit-icon">✓</span>
                        <div>
                          <strong>Get Verified Badge</strong>
                          <span>Stand out with a verified creator badge</span>
                        </div>
                      </div>
                      <div className="benefit">
                        <span className="benefit-icon">✓</span>
                        <div>
                          <strong>More Campaign Invites</strong>
                          <span>Brands prefer verified creators for campaigns</span>
                        </div>
                      </div>
                    </div>

                    <button 
                      className="btn-connect-instagram-large"
                      onClick={handleInstagramConnect}
                      disabled={instagramConnecting}
                    >
                      <span className="ig-gradient-icon">📸</span>
                      {instagramConnecting ? 'Connecting...' : 'Connect Instagram Account'}
                    </button>
                    
                    <p className="privacy-note">
                      We only access your public profile information and recent posts. 
                      We never post on your behalf without permission.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Preferences Tab */}
          {activeTab === 'preferences' && (
            <div className="tab-content">
              <h1>Creator Preferences</h1>
              <p className="preferences-subtitle">
                Set your preferences to help brands find and match with you for campaigns.
              </p>

              {hasUnsavedPreferences && (
                <div className="unsaved-changes-banner" style={{
                  background: '#fef3c7',
                  color: '#92400e',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <span>⚠️ You have unsaved changes</span>
                  <button 
                    onClick={handleSavePreferences} 
                    disabled={preferencesSaving}
                    style={{
                      background: '#92400e',
                      color: 'white',
                      border: 'none',
                      padding: '6px 16px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    {preferencesSaving ? 'Saving...' : 'Save Now'}
                  </button>
                </div>
              )}

              {preferencesMessage && (
                <div className={`preferences-message ${preferencesMessage.type}`}>
                  <span>{preferencesMessage.text}</span>
                  <button onClick={() => setPreferencesMessage(null)}>×</button>
                </div>
              )}

              {preferencesLoading ? (
                <div className="preferences-loading">
                  <div className="loading-spinner"></div>
                  <p>Loading preferences...</p>
                </div>
              ) : (
                <div className="preferences-form">
                  {/* Availability */}
                  <div className="preferences-section">
                    <h3>Availability</h3>
                    <div className="preference-toggle">
                      <label className="toggle-label">
                        <input 
                          type="checkbox" 
                          checked={preferences.available_for_campaigns}
                          onChange={(e) => handlePreferenceChange('available_for_campaigns', e.target.checked)}
                        />
                        <span className="toggle-slider"></span>
                        <span className="toggle-text">Available for campaign opportunities</span>
                      </label>
                    </div>
                  </div>

                  {/* Collaboration Types */}
                  <div className="preferences-section">
                    <h3>Collaboration Types</h3>
                    <p className="section-hint">Select the types of collaborations you're interested in</p>
                    <div className="chip-grid">
                      {['Sponsored Post', 'Product Review', 'Brand Ambassador', 'Affiliate', 'Giveaway', 'Unboxing', 'Tutorial', 'Story Mention'].map(type => (
                        <button
                          key={type}
                          className={`chip ${preferences.collaboration_types?.includes(type.toLowerCase().replace(' ', '_')) ? 'selected' : ''}`}
                          onClick={() => toggleArrayItem('collaboration_types', type.toLowerCase().replace(' ', '_'))}
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Content Categories */}
                  <div className="preferences-section">
                    <h3>Content Categories</h3>
                    <p className="section-hint">What topics do you create content about?</p>
                    <div className="chip-grid">
                      {['Fashion', 'Beauty', 'Fitness', 'Food', 'Travel', 'Tech', 'Gaming', 'Lifestyle', 'Sports', 'Music', 'Photography', 'Art', 'Business', 'Education', 'Entertainment', 'Parenting', 'Pets', 'Automotive'].map(cat => (
                        <button
                          key={cat}
                          className={`chip ${preferences.content_categories?.includes(cat.toLowerCase()) ? 'selected' : ''}`}
                          onClick={() => toggleArrayItem('content_categories', cat.toLowerCase())}
                        >
                          {cat}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Pricing */}
                  <div className="preferences-section">
                    <h3>Your Rates (INR)</h3>
                    <p className="section-hint">Set your starting rates for different content types</p>
                    <div className="rates-grid">
                      <div className="rate-input-group">
                        <label>Per Post</label>
                        <div className="rate-input-wrapper">
                          <span className="currency">₹</span>
                          <input
                            type="number"
                            placeholder="5000"
                            value={preferences.rate_per_post}
                            onChange={(e) => handlePreferenceChange('rate_per_post', e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="rate-input-group">
                        <label>Per Reel</label>
                        <div className="rate-input-wrapper">
                          <span className="currency">₹</span>
                          <input
                            type="number"
                            placeholder="10000"
                            value={preferences.rate_per_reel}
                            onChange={(e) => handlePreferenceChange('rate_per_reel', e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="rate-input-group">
                        <label>Per Story</label>
                        <div className="rate-input-wrapper">
                          <span className="currency">₹</span>
                          <input
                            type="number"
                            placeholder="2000"
                            value={preferences.rate_per_story}
                            onChange={(e) => handlePreferenceChange('rate_per_story', e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="rate-input-group">
                        <label>Min Campaign Budget</label>
                        <div className="rate-input-wrapper">
                          <span className="currency">₹</span>
                          <input
                            type="number"
                            placeholder="15000"
                            value={preferences.min_campaign_budget}
                            onChange={(e) => handlePreferenceChange('min_campaign_budget', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Brand Industries */}
                  <div className="preferences-section">
                    <h3>Preferred Brand Industries</h3>
                    <p className="section-hint">Industries you'd like to work with (leave empty for all)</p>
                    <div className="chip-grid">
                      {['Fashion & Apparel', 'Beauty & Skincare', 'Food & Beverage', 'Technology', 'Health & Wellness', 'Travel & Tourism', 'Finance', 'Education', 'Entertainment', 'E-commerce', 'Real Estate', 'Automotive'].map(industry => (
                        <button
                          key={industry}
                          className={`chip ${preferences.preferred_brand_industries?.includes(industry.toLowerCase()) ? 'selected' : ''}`}
                          onClick={() => toggleArrayItem('preferred_brand_industries', industry.toLowerCase())}
                        >
                          {industry}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Excluded Industries */}
                  <div className="preferences-section">
                    <h3>Industries to Avoid</h3>
                    <p className="section-hint">Select industries you won't work with</p>
                    <div className="chip-grid">
                      {['Alcohol', 'Tobacco', 'Gambling', 'Adult Content', 'Political', 'Cryptocurrency', 'MLM/Network Marketing', 'Weight Loss Pills'].map(industry => (
                        <button
                          key={industry}
                          className={`chip exclude ${preferences.excluded_brand_industries?.includes(industry.toLowerCase()) ? 'selected' : ''}`}
                          onClick={() => toggleArrayItem('excluded_brand_industries', industry.toLowerCase())}
                        >
                          {industry}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Languages */}
                  <div className="preferences-section">
                    <h3>Languages Spoken</h3>
                    <p className="section-hint">Languages you can create content in</p>
                    <div className="chip-grid">
                      {['English', 'Hindi', 'Tamil', 'Telugu', 'Kannada', 'Malayalam', 'Marathi', 'Bengali', 'Gujarati', 'Punjabi'].map(lang => (
                        <button
                          key={lang}
                          className={`chip ${preferences.languages_spoken?.includes(lang.toLowerCase()) ? 'selected' : ''}`}
                          onClick={() => toggleArrayItem('languages_spoken', lang.toLowerCase())}
                        >
                          {lang}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Target Locations */}
                  <div className="preferences-section">
                    <h3>Target Locations</h3>
                    <p className="section-hint">Cities or regions your audience is primarily from</p>
                    <div className="chip-grid">
                      {['Mumbai', 'Delhi', 'Bangalore', 'Hyderabad', 'Chennai', 'Kolkata', 'Pune', 'Ahmedabad', 'Jaipur', 'Pan India', 'International'].map(loc => (
                        <button
                          key={loc}
                          className={`chip ${preferences.target_locations?.includes(loc.toLowerCase()) ? 'selected' : ''}`}
                          onClick={() => toggleArrayItem('target_locations', loc.toLowerCase())}
                        >
                          {loc}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Response Time */}
                  <div className="preferences-section">
                    <h3>Response Time</h3>
                    <p className="section-hint">How quickly do you typically respond to campaign inquiries?</p>
                    <div className="response-time-options">
                      {[
                        { value: 6, label: 'Within 6 hours' },
                        { value: 12, label: 'Within 12 hours' },
                        { value: 24, label: 'Within 24 hours' },
                        { value: 48, label: 'Within 2 days' },
                        { value: 72, label: 'Within 3 days' }
                      ].map(option => (
                        <button
                          key={option.value}
                          className={`response-option ${preferences.response_time_hours === option.value ? 'selected' : ''}`}
                          onClick={() => handlePreferenceChange('response_time_hours', option.value)}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="preferences-actions">
                    <button 
                      className="btn-save-preferences"
                      onClick={handleSavePreferences}
                      disabled={preferencesSaving}
                    >
                      {preferencesSaving ? 'Saving...' : 'Save Preferences'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Payment Details Tab */}
          {activeTab === 'payment' && (
            <div className="tab-content">
              <h1>Payment Details</h1>
              <p className="tab-subtitle">Add your payment details to receive campaign earnings</p>
              
              {!paymentDetails && (
                <div className="payment-warning-banner">
                  <span className="warning-icon">⚠️</span>
                  <div>
                    <strong>Payment details required</strong>
                    <p>You must add payment details before accepting any campaign invitations.</p>
                  </div>
                </div>
              )}

              {paymentDetailsMessage && (
                <div className={`message ${paymentDetailsMessage.type}`}>
                  {paymentDetailsMessage.text}
                </div>
              )}

              {paymentDetailsLoading ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>Loading payment details...</p>
                </div>
              ) : (
                <div className="payment-details-section">
                  {/* Current Payment Details (if exists) */}
                  {paymentDetails && (
                    <div className="current-payment-info">
                      <h3>✅ Current Payment Method</h3>
                      <div className="payment-display-card">
                        {paymentDetails.payment_method === 'upi' ? (
                          <>
                            <span className="payment-type-badge">UPI</span>
                            <div className="payment-value">
                              <strong>UPI ID:</strong> {paymentDetails.upi_id}
                            </div>
                          </>
                        ) : (
                          <>
                            <span className="payment-type-badge">Bank Account</span>
                            <div className="payment-value">
                              <strong>Account Holder:</strong> {paymentDetails.account_holder_name}
                            </div>
                            <div className="payment-value">
                              <strong>Account Number:</strong> ****{paymentDetails.account_number?.slice(-4)}
                            </div>
                            <div className="payment-value">
                              <strong>IFSC:</strong> {paymentDetails.ifsc_code}
                            </div>
                            {paymentDetails.bank_name && (
                              <div className="payment-value">
                                <strong>Bank:</strong> {paymentDetails.bank_name}
                              </div>
                            )}
                          </>
                        )}
                        {paymentDetails.pan_number && (
                          <div className="payment-value">
                            <strong>PAN:</strong> {paymentDetails.pan_number.slice(0, 2)}****{paymentDetails.pan_number.slice(-2)}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Payment Details Form */}
                  <div className="payment-form-section">
                    <h3>{paymentDetails ? 'Update Payment Details' : 'Add Payment Details'}</h3>
                    
                    {/* Payment Method Toggle */}
                    <div className="payment-method-toggle">
                      <button
                        className={`method-btn ${paymentMethod === 'upi' ? 'active' : ''}`}
                        onClick={() => setPaymentMethod('upi')}
                        type="button"
                      >
                        <span className="method-icon">📱</span>
                        <span>UPI</span>
                        <span className="method-hint">Instant & Free</span>
                      </button>
                      <button
                        className={`method-btn ${paymentMethod === 'bank' ? 'active' : ''}`}
                        onClick={() => setPaymentMethod('bank')}
                        type="button"
                      >
                        <span className="method-icon">🏦</span>
                        <span>Bank Account</span>
                        <span className="method-hint">NEFT/IMPS</span>
                      </button>
                    </div>

                    <form onSubmit={handleSavePaymentDetails} className="payment-form">
                      {paymentMethod === 'upi' ? (
                        <div className="form-group">
                          <label>UPI ID *</label>
                          <input
                            type="text"
                            placeholder="yourname@upi (e.g., 9876543210@paytm)"
                            value={paymentForm.upi_id}
                            onChange={(e) => setPaymentForm(prev => ({ ...prev, upi_id: e.target.value }))}
                            required
                          />
                          <span className="hint">Enter your UPI ID linked to any UPI app (GPay, PhonePe, Paytm, etc.)</span>
                        </div>
                      ) : (
                        <>
                          <div className="form-group">
                            <label>Account Holder Name *</label>
                            <input
                              type="text"
                              placeholder="As per bank records"
                              value={paymentForm.account_holder_name}
                              onChange={(e) => setPaymentForm(prev => ({ ...prev, account_holder_name: e.target.value }))}
                              required
                            />
                          </div>
                          <div className="form-group">
                            <label>Account Number *</label>
                            <input
                              type="text"
                              placeholder="Enter account number"
                              value={paymentForm.account_number}
                              onChange={(e) => setPaymentForm(prev => ({ ...prev, account_number: e.target.value.replace(/\D/g, '') }))}
                              required
                            />
                          </div>
                          <div className="form-row">
                            <div className="form-group">
                              <label>IFSC Code *</label>
                              <input
                                type="text"
                                placeholder="e.g., SBIN0001234"
                                value={paymentForm.ifsc_code}
                                onChange={(e) => setPaymentForm(prev => ({ ...prev, ifsc_code: e.target.value.toUpperCase() }))}
                                maxLength={11}
                                required
                              />
                            </div>
                            <div className="form-group">
                              <label>Bank Name</label>
                              <input
                                type="text"
                                placeholder="e.g., State Bank of India"
                                value={paymentForm.bank_name}
                                onChange={(e) => setPaymentForm(prev => ({ ...prev, bank_name: e.target.value }))}
                              />
                            </div>
                          </div>
                          <div className="form-group">
                            <label>Account Type</label>
                            <div className="account-type-options">
                              <button
                                type="button"
                                className={`account-type-btn ${paymentForm.account_type === 'savings' ? 'active' : ''}`}
                                onClick={() => setPaymentForm(prev => ({ ...prev, account_type: 'savings' }))}
                              >
                                Savings
                              </button>
                              <button
                                type="button"
                                className={`account-type-btn ${paymentForm.account_type === 'current' ? 'active' : ''}`}
                                onClick={() => setPaymentForm(prev => ({ ...prev, account_type: 'current' }))}
                              >
                                Current
                              </button>
                            </div>
                          </div>
                        </>
                      )}

                      {/* PAN Number (optional) */}
                      <div className="form-group">
                        <label>PAN Number (Optional)</label>
                        <input
                          type="text"
                          placeholder="ABCDE1234F"
                          value={paymentForm.pan_number}
                          onChange={(e) => setPaymentForm(prev => ({ ...prev, pan_number: e.target.value.toUpperCase() }))}
                          maxLength={10}
                        />
                        <span className="hint">Required for earnings above ₹50,000/year (TDS compliance)</span>
                      </div>

                      <div className="form-actions">
                        <button 
                          type="submit" 
                          className="btn-save-payment"
                          disabled={paymentDetailsSaving}
                        >
                          {paymentDetailsSaving ? 'Saving...' : (paymentDetails ? 'Update Payment Details' : 'Save Payment Details')}
                        </button>
                      </div>
                    </form>
                  </div>

                  {/* Security Note */}
                  <div className="payment-security-note">
                    <span className="security-icon">🔒</span>
                    <div>
                      <strong>Your information is secure</strong>
                      <p>We use industry-standard encryption to protect your payment details. Your data is never shared with third parties.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </main>
      </div>

      {/* Content Submission Modal */}
      {showSubmissionModal && selectedCampaignForSubmission && (
        <div className="modal-overlay" onClick={() => setShowSubmissionModal(false)}>
          <div className="submission-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📤 Submit Content for Review</h2>
              <button className="modal-close" onClick={() => setShowSubmissionModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="campaign-info-box">
                <h4>{selectedCampaignForSubmission.campaign_name}</h4>
                <span className="brand">{selectedCampaignForSubmission.brand_name}</span>
              </div>
              
              {selectedCampaignForSubmission.content_brief && (
                <div className="brief-reminder">
                  <h5>📋 Brief Reminder</h5>
                  <p>{selectedCampaignForSubmission.content_brief.brief?.substring(0, 200)}...</p>
                </div>
              )}
              
              {submissionError && (
                <div className="submission-error">{submissionError}</div>
              )}
              
              {/* Submission Type Toggle */}
              <div className="submission-type-toggle">
                <button 
                  className={`toggle-btn ${submissionType === 'url' ? 'active' : ''}`}
                  onClick={() => setSubmissionType('url')}
                  type="button"
                >
                  🔗 Paste URL
                </button>
                <button 
                  className={`toggle-btn ${submissionType === 'file' ? 'active' : ''}`}
                  onClick={() => setSubmissionType('file')}
                  type="button"
                >
                  📁 Upload File
                </button>
              </div>
              
              {submissionType === 'url' ? (
                <div className="form-group">
                  <label>Content URL *</label>
                  <input
                    type="url"
                    placeholder="https://drive.google.com/... or Dropbox link"
                    value={contentUrl}
                    onChange={(e) => setContentUrl(e.target.value)}
                  />
                  <span className="hint">Upload to Google Drive, Dropbox, or WeTransfer and paste the link</span>
                </div>
              ) : (
                <div className="form-group">
                  <label>Upload Content *</label>
                  <div className="file-upload-area">
                    <input
                      type="file"
                      id="content-file"
                      accept="image/*,video/*"
                      onChange={(e) => setSelectedFile(e.target.files[0])}
                      style={{ display: 'none' }}
                    />
                    <label htmlFor="content-file" className="file-upload-label">
                      {selectedFile ? (
                        <div className="selected-file">
                          <span className="file-icon">
                            {selectedFile.type.startsWith('video') ? '🎬' : '🖼️'}
                          </span>
                          <span className="file-name">{selectedFile.name}</span>
                          <span className="file-size">({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)</span>
                        </div>
                      ) : (
                        <div className="upload-placeholder">
                          <span className="upload-icon">📤</span>
                          <span>Click to select image or video</span>
                          <span className="hint">Max 100MB • MP4, MOV, JPG, PNG</span>
                        </div>
                      )}
                    </label>
                  </div>
                  {uploadProgress > 0 && uploadProgress < 100 && (
                    <div className="upload-progress">
                      <div className="progress-bar" style={{ width: `${uploadProgress}%` }}></div>
                      <span>{uploadProgress}%</span>
                    </div>
                  )}
                </div>
              )}
              
              <div className="form-group">
                <label>Notes for Brand (optional)</label>
                <textarea
                  placeholder="Any notes about the content, creative choices, etc."
                  value={submissionNotes}
                  onChange={(e) => setSubmissionNotes(e.target.value)}
                  rows={3}
                />
              </div>
              
              {selectedCampaignForSubmission.task && (
                <div className="submission-info">
                  <span>Submission {(selectedCampaignForSubmission.task.submission_count || 0) + 1} of {selectedCampaignForSubmission.task.max_submissions || 3}</span>
                </div>
              )}
            </div>
            
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowSubmissionModal(false)}>
                Cancel
              </button>
              <button 
                className="btn-submit"
                onClick={handleContentSubmission}
                disabled={contentSubmitting}
              >
                {contentSubmitting ? 'Submitting...' : 'Submit for Review'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Publish URL Modal */}
      {showPublishModal && selectedCampaignForSubmission && (
        <div className="modal-overlay" onClick={() => setShowPublishModal(false)}>
          <div className="submission-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🚀 Submit Published Content URL</h2>
              <button className="modal-close" onClick={() => setShowPublishModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="campaign-info-box">
                <h4>{selectedCampaignForSubmission.campaign_name}</h4>
                <span className="brand">{selectedCampaignForSubmission.brand_name}</span>
              </div>

              {(!instagramStatus.connected || instagramStatus.needs_reconnect || instagramStatus.token_valid === false) ? (
                <div className="publish-info" style={{ borderLeft: '3px solid #f59e0b', background: '#fffbeb' }}>
                  {instagramStatus.connected && (instagramStatus.needs_reconnect || instagramStatus.token_valid === false) ? (
                    <>
                      <p><strong>Your Instagram session has expired.</strong></p>
                      <p>Reconnect your Instagram account so we can verify this URL and track performance for your payouts.</p>
                    </>
                  ) : (
                    <>
                      <p><strong>Instagram not connected.</strong></p>
                      <p>We verify each published URL against your Instagram account and use it to track performance. Connect Instagram to continue.</p>
                    </>
                  )}
                  {submissionError && (
                    <div className="submission-error" style={{ marginTop: 12 }}>{submissionError}</div>
                  )}
                  <button
                    className="btn-connect-instagram"
                    style={{ marginTop: 12 }}
                    onClick={handleInstagramConnect}
                    disabled={instagramConnecting}
                  >
                    {instagramConnecting ? 'Connecting…' : (instagramStatus.connected ? 'Reconnect Instagram' : 'Connect Instagram')}
                  </button>
                </div>
              ) : (
                <>
                  <div className="publish-info">
                    <p>Your content has been approved! Now publish it on Instagram and paste the URL below.</p>
                    <p className="highlight">📊 Performance monitoring will begin once you submit the URL.</p>
                    <p style={{ fontSize: 13, color: '#6b7280', marginTop: 6 }}>
                      Submitting from <strong>@{instagramStatus.username}</strong>. The URL must be a post or reel on this account.
                    </p>
                  </div>

                  {submissionError && (
                    <div className="submission-error">{submissionError}</div>
                  )}

                  <div className="form-group">
                    <label>Instagram Post/Reel URL *</label>
                    <input
                      type="url"
                      placeholder="https://www.instagram.com/reel/... or /p/..."
                      value={platformUrl}
                      onChange={(e) => setPlatformUrl(e.target.value)}
                    />
                    <span className="hint">Copy the URL from your published Instagram post or reel</span>
                  </div>
                </>
              )}
            </div>

            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowPublishModal(false)}>
                Cancel
              </button>
              {instagramStatus.connected && !instagramStatus.needs_reconnect && instagramStatus.token_valid !== false && (
                <button
                  className="btn-submit"
                  onClick={handlePublishUrlSubmission}
                  disabled={contentSubmitting}
                >
                  {contentSubmitting ? 'Submitting...' : 'Submit & Start Monitoring'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

export default CreatorDashboard;
